import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import Swal from "sweetalert2";

import {
    FaBookOpen,
    FaCheckCircle,
    FaClock,
    FaExclamationTriangle,
    FaGraduationCap,
    FaPlus,
    FaShoppingCart,
    FaTrash,
    FaUsers,
    FaUserTie,
    FaVideo,
} from "react-icons/fa";

import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";

const formatMoney = (value) => {
    const amount = Number(value || 0);

    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

const getErrorMessage = (
    error,
    fallback = "Something went wrong."
) => {
    const data = error?.response?.data;

    if (!data) {
        return error?.message || fallback;
    }

    if (typeof data === "string") {
        return data;
    }

    if (data.detail) {
        return data.detail;
    }

    if (data.message) {
        return data.message;
    }

    if (data.error) {
        return data.error;
    }

    if (data.non_field_errors?.length) {
        return data.non_field_errors[0];
    }

    const firstKey = Object.keys(data)[0];

    if (firstKey) {
        const value = data[firstKey];

        if (Array.isArray(value)) {
            return value[0];
        }

        if (typeof value === "string") {
            return value;
        }

        if (
            value &&
            typeof value === "object"
        ) {
            if (value.detail) {
                return value.detail;
            }

            if (value.message) {
                return value.message;
            }
        }
    }

    return fallback;
};

const getErrorCode = (error) => {
    const data = error?.response?.data;

    if (!data || typeof data !== "object") {
        return null;
    }

    return (
        data.code ||
        data.error_code ||
        data.errorCode ||
        null
    );
};

const cayepSwal = Swal.mixin({
    confirmButtonColor: "#062A4B",
    cancelButtonColor: "#6B7280",
    background: "#FFFFFF",
    color: "#142536",
});

const BANKS = [
    "LBDI",
    "Ecobank Liberia",
    "International Bank Liberia",
    "Bloom Bank Africa",
    "Sapelle International Bank",
    "UBA Liberia",
    "Access Bank Liberia",
    "GTBank Liberia",
    "Afriland First Bank Liberia",
];

const getSlotState = (course) => {
    const rawTotal = Number(
        course?.slots ?? 0
    );

    const rawTaken = Number(
        course?.slots_taken ?? 0
    );

    const total = Number.isFinite(rawTotal)
        ? Math.max(0, rawTotal)
        : 0;

    const taken = Number.isFinite(rawTaken)
        ? Math.max(0, rawTaken)
        : 0;

    const backendStatus = String(
        course?.slot_status || ""
    ).toLowerCase();

    const unlimited =
        backendStatus === "unlimited" ||
        (
            !backendStatus &&
            total === 0 &&
            course?.is_full !== true
        );

    const rawRemaining =
        course?.slots_remaining;

    const parsedRemaining =
        Number(rawRemaining);

    const calculatedRemaining =
        Math.max(
            total - taken,
            0
        );

    const remaining = unlimited
        ? null
        : Number.isFinite(parsedRemaining)
        ? Math.max(
              0,
              parsedRemaining
          )
        : calculatedRemaining;

    const full =
        backendStatus === "full" ||
        course?.is_full === true ||
        (
            !unlimited &&
            remaining <= 0
        );

    const lastSlot =
        !full &&
        (
            backendStatus === "last_slot" ||
            (
                !unlimited &&
                remaining === 1
            )
        );

    return {
        total,
        taken,
        remaining,
        full,
        lastSlot,
        unlimited,
    };
};

const getSlotLabel = (course) => {
    const slotState =
        getSlotState(course);

    if (slotState.full) {
        return "Class Full";
    }

    if (slotState.unlimited) {
        return "Open";
    }

    if (slotState.lastSlot) {
        return "Last Slot";
    }

    return `${slotState.remaining} Available`;
};

const getCourseFromPlan = (item) => {
    if (!item) {
        return null;
    }

    return (
        item.course ||
        item.subject ||
        null
    );
};

const getPlanIdFromItem = (item) => {
    if (!item) {
        return null;
    }

    return (
        item.id ||
        item.plan_id ||
        item.course_plan_id ||
        null
    );
};

const getCourseIdFromPlan = (item) => {
    const course =
        getCourseFromPlan(item);

    if (course?.id != null) {
        return Number(course.id);
    }

    if (item?.course_id != null) {
        return Number(item.course_id);
    }

    if (item?.subject_id != null) {
        return Number(item.subject_id);
    }

    return null;
};

const normalizePlanItems = (items) => {
    if (!Array.isArray(items)) {
        return [];
    }

    const seenPlanIds = new Set();
    const seenCourseIds = new Set();

    return items.filter((item) => {
        const planId =
            getPlanIdFromItem(item);

        const courseId =
            getCourseIdFromPlan(item);

        if (
            planId != null &&
            seenPlanIds.has(planId)
        ) {
            return false;
        }

        if (
            courseId != null &&
            seenCourseIds.has(courseId)
        ) {
            return false;
        }

        if (planId != null) {
            seenPlanIds.add(planId);
        }

        if (courseId != null) {
            seenCourseIds.add(courseId);
        }

        return true;
    });
};

const PlanCoursesPage = () => {
    const { selectedYear } =
        useAcademicYear();

    const [courses, setCourses] =
        useState([]);

    const [cart, setCart] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [cartLoading, setCartLoading] =
        useState(false);

    const [cartOpen, setCartOpen] =
        useState(false);

    const [search, setSearch] =
        useState("");

    const [category, setCategory] =
        useState("");

    const [level, setLevel] =
        useState("");

    const [page, setPage] =
        useState(1);

    const [pageSize] =
        useState(12);

    const [totalPages, setTotalPages] =
        useState(1);

    const [
        paymentMethod,
        setPaymentMethod,
    ] = useState("bank_deposit");

    const [bankName, setBankName] =
        useState("");

    const [
        transactionReference,
        setTransactionReference,
    ] = useState("");

    const [paymentAmount, setPaymentAmount] =
        useState("");

    const [submitting, setSubmitting] =
        useState(false);

    const [eligibility, setEligibility] =
        useState(null);

    const [
        eligibilityLoading,
        setEligibilityLoading,
    ] = useState(true);

    const [
        refreshingSlots,
        setRefreshingSlots,
    ] = useState(false);

    const fetchEligibility =
        useCallback(async () => {
            setEligibilityLoading(true);

            try {
                const response =
                    await api.get(
                        "/api/course-learning/eligibility/status/"
                    );

                setEligibility(
                    response.data
                );
            } catch (error) {
                console.error(
                    "Eligibility error:",
                    error
                );

                setEligibility({
                    eligible: false,
                    message:
                        getErrorMessage(
                            error,
                            "Unable to verify course eligibility."
                        ),
                });
            } finally {
                setEligibilityLoading(
                    false
                );
            }
        }, []);

    const fetchCart =
        useCallback(async () => {
            setCartLoading(true);

            try {
                const response =
                    await api.get(
                        "/api/course-learning/plans/"
                    );

                const data =
                    response.data;

                const items =
                    Array.isArray(data)
                        ? data
                        : data?.items ||
                          data?.results ||
                          [];

                setCart(
                    normalizePlanItems(
                        items
                    )
                );
            } catch (error) {
                console.error(
                    "Cart error:",
                    error
                );

                setCart([]);
            } finally {
                setCartLoading(false);
            }
        }, []);

    const fetchCourses =
        useCallback(async () => {
            setLoading(true);

            try {
                const params = {
                    page,
                    page_size: pageSize,
                };

                if (search.trim()) {
                    params.search =
                        search.trim();
                }

                if (category) {
                    params.category =
                        category;
                }

                if (level) {
                    params.level =
                        level;
                }

                const response =
                    await api.get(
                        "/api/course-learning/courses/",
                        {
                            params,
                        }
                    );

                const data =
                    response.data;

                if (Array.isArray(data)) {
                    setCourses(data);
                    setTotalPages(1);
                    return;
                }

                const results =
                    data?.results ||
                    data?.items ||
                    [];

                setCourses(results);

                const count =
                    Number(
                        data?.count || 0
                    );

                if (count > 0) {
                    setTotalPages(
                        Math.max(
                            1,
                            Math.ceil(
                                count /
                                    pageSize
                            )
                        )
                    );
                } else {
                    setTotalPages(
                        results.length > 0
                            ? Math.max(
                                  1,
                                  page
                              )
                            : 1
                    );
                }
            } catch (error) {
                console.error(
                    "Courses error:",
                    error
                );

                setCourses([]);
                setTotalPages(1);

                await cayepSwal.fire({
                    icon: "error",
                    title:
                        "Unable to Load Courses",
                    text: getErrorMessage(
                        error,
                        "The course catalog could not be loaded."
                    ),
                });
            } finally {
                setLoading(false);
            }
        }, [
            category,
            level,
            page,
            pageSize,
            search,
        ]);

    const refreshCourseData =
        useCallback(async () => {
            setRefreshingSlots(true);

            try {
                await Promise.all([
                    fetchCourses(),
                    fetchCart(),
                ]);
            } finally {
                setRefreshingSlots(false);
            }
        }, [
            fetchCourses,
            fetchCart,
        ]);

    useEffect(() => {
        fetchEligibility();
    }, [fetchEligibility]);

    useEffect(() => {
        fetchCart();
    }, [
        fetchCart,
        selectedYear,
    ]);

    useEffect(() => {
        fetchCourses();
    }, [
        fetchCourses,
        selectedYear,
    ]);

    useEffect(() => {
        setPage(1);
    }, [
        search,
        category,
        level,
    ]);

    useEffect(() => {
        const refreshTimer =
            window.setInterval(() => {
                refreshCourseData();
            }, 60000);

        return () => {
            window.clearInterval(
                refreshTimer
            );
        };
    }, [refreshCourseData]);

    const isEligible = useMemo(() => {
        if (!eligibility) {
            return false;
        }

        return Boolean(
            eligibility.eligible ??
                eligibility.is_eligible ??
                eligibility.can_plan_courses ??
                eligibility.can_plan
        );
    }, [eligibility]);

    const isInCart =
        useCallback(
            (courseId) => {
                const normalizedCourseId =
                    Number(courseId);

                if (
                    !Number.isFinite(
                        normalizedCourseId
                    )
                ) {
                    return false;
                }

                return cart.some(
                    (item) =>
                        getCourseIdFromPlan(
                            item
                        ) ===
                        normalizedCourseId
                );
            },
            [cart]
        );

    const getCartCourse =
        useCallback((item) => {
            return (
                getCourseFromPlan(
                    item
                ) || item
            );
        }, []);

    const getPlanId =
        useCallback((item) => {
            return getPlanIdFromItem(
                item
            );
        }, []);

    const addToCart = async (
        course
    ) => {
        if (!isEligible) {
            await cayepSwal.fire({
                icon: "warning",
                title: "Not Eligible",
                text:
                    eligibility?.message ||
                    "You are not currently eligible to plan courses.",
            });

            return;
        }

        const slotState =
            getSlotState(course);

        if (slotState.full) {
            await cayepSwal.fire({
                icon: "warning",
                title: "Class Full",
                text:
                    "This course has reached its maximum capacity.",
            });

            await fetchCourses();

            return;
        }

        if (isInCart(course.id)) {
            await cayepSwal.fire({
                icon: "info",
                title: "Already Planned",
                text:
                    "This course is already in your planned courses.",
            });

            return;
        }

        try {
            setCartLoading(true);

            await api.post(
                "/api/course-learning/plans/add/",
                {
                    course_id:
                        course.id,
                }
            );

            await Promise.all([
                fetchCart(),
                fetchCourses(),
            ]);

            await cayepSwal.fire({
                icon: "success",
                title: "Course Added",
                text: `${
                    course.course_title ||
                    course.name
                } has been added to your planned courses.`,
                timer: 1800,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error(
                "Add course error:",
                error
            );

            const errorCode =
                getErrorCode(error);

            const errorText =
                String(
                    getErrorMessage(
                        error,
                        ""
                    )
                ).toLowerCase();

            await Promise.all([
                fetchCourses(),
                fetchCart(),
            ]);

            if (
                errorCode ===
                    "COURSE_FULL" ||
                errorCode ===
                    "NO_SLOTS" ||
                errorText.includes(
                    "capacity"
                ) ||
                errorText.includes(
                    "class full"
                )
            ) {
                await cayepSwal.fire({
                    icon: "warning",
                    title:
                        "Class Full",
                    text: getErrorMessage(
                        error,
                        "This course has just reached its maximum capacity."
                    ),
                });

                return;
            }

            if (
                errorCode ===
                "ALREADY_ENROLLED"
            ) {
                await cayepSwal.fire({
                    icon: "info",
                    title:
                        "Already Enrolled",
                    text:
                        "You are already enrolled in this course for the current training year.",
                });

                return;
            }

            if (
                errorCode ===
                "COURSE_COMPLETED"
            ) {
                await cayepSwal.fire({
                    icon: "info",
                    title:
                        "Course Completed",
                    text:
                        "You have already completed this course and cannot plan it again.",
                });

                return;
            }

            if (
                errorCode ===
                "ALREADY_PLANNED"
            ) {
                await fetchCart();

                await cayepSwal.fire({
                    icon: "info",
                    title:
                        "Already Planned",
                    text:
                        "This course is already in your planned courses.",
                });

                return;
            }

            await cayepSwal.fire({
                icon: "error",
                title:
                    "Unable to Add Course",
                text: getErrorMessage(
                    error,
                    "The course could not be added to your planned courses."
                ),
            });
        } finally {
            setCartLoading(false);
        }
    };

    const removeFromCart =
        async (planId) => {
            if (!planId) {
                await cayepSwal.fire({
                    icon: "error",
                    title:
                        "Invalid Course Plan",
                    text:
                        "The selected course plan could not be identified.",
                });

                return;
            }

            const result =
                await cayepSwal.fire({
                    icon: "question",
                    title:
                        "Remove Course?",
                    text:
                        "This course will be removed from your planned courses.",
                    showCancelButton: true,
                    confirmButtonText:
                        "Remove",
                    cancelButtonText:
                        "Keep Course",
                });

            if (
                !result.isConfirmed
            ) {
                return;
            }

            try {
                setCartLoading(true);

                await api.delete(
                    `/api/course-learning/plans/${planId}/remove/`
                );

                setCart((current) =>
                    current.filter(
                        (item) =>
                            Number(
                                getPlanIdFromItem(
                                    item
                                )
                            ) !==
                            Number(planId)
                    )
                );

                await Promise.all([
                    fetchCart(),
                    fetchCourses(),
                ]);

                await cayepSwal.fire({
                    icon: "success",
                    title:
                        "Course Removed",
                    text:
                        "The course has been removed from your planned courses.",
                    timer: 1800,
                    showConfirmButton:
                        false,
                });
            } catch (error) {
                console.error(
                    "Remove course error:",
                    error
                );

                await Promise.all([
                    fetchCart(),
                    fetchCourses(),
                ]);

                await cayepSwal.fire({
                    icon: "error",
                    title:
                        "Unable to Remove Course",
                    text: getErrorMessage(
                        error,
                        "The planned course could not be removed."
                    ),
                });
            } finally {
                setCartLoading(false);
            }
        };

    const submitPlan =
        async () => {
            if (!cart.length) {
                await cayepSwal.fire({
                    icon: "warning",
                    title:
                        "No Courses Selected",
                    text:
                        "Please add at least one course before submitting.",
                });

                return;
            }

            if (!paymentMethod) {
                await cayepSwal.fire({
                    icon: "warning",
                    title:
                        "Payment Method Required",
                    text:
                        "Please select a payment method.",
                });

                return;
            }

            if (
                paymentMethod ===
                    "bank_deposit" &&
                !bankName
            ) {
                await cayepSwal.fire({
                    icon: "warning",
                    title:
                        "Bank Required",
                    text:
                        "Please select the bank used for your payment.",
                });

                return;
            }

            if (
                !transactionReference.trim()
            ) {
                await cayepSwal.fire({
                    icon: "warning",
                    title:
                        "Transaction Reference Required",
                    text:
                        "Please enter your payment transaction reference.",
                });

                return;
            }

            if (
                !paymentAmount ||
                Number(paymentAmount) <=
                    0
            ) {
                await cayepSwal.fire({
                    icon: "warning",
                    title:
                        "Payment Amount Required",
                    text:
                        "Please enter a valid payment amount.",
                });

                return;
            }

            const result =
                await cayepSwal.fire({
                    icon: "question",
                    title:
                        "Submit Course Plan?",
                    html: `
                        <div style="text-align:left;">
                            <p>
                                You are submitting
                                <strong>${cart.length}</strong>
                                course${
                                    cart.length === 1
                                        ? ""
                                        : "s"
                                }.
                            </p>
                            <p>
                                Total course value:
                                <strong>
                                    USD ${formatMoney(
                                        cartTotal
                                    )}
                                </strong>
                            </p>
                        </div>
                    `,
                    showCancelButton: true,
                    confirmButtonText:
                        "Submit Plan",
                    cancelButtonText:
                        "Review",
                });

            if (
                !result.isConfirmed
            ) {
                return;
            }

            try {
                setSubmitting(true);

                await api.post(
                    "/api/course-learning/orders/submit/",
                    {
                        payment_method:
                            paymentMethod,

                        bank_name:
                            paymentMethod ===
                            "bank_deposit"
                                ? bankName
                                : "",

                        reference_number:
                            transactionReference.trim(),

                        notes: "",
                    }
                );

                setCart([]);
                setTransactionReference(
                    ""
                );
                setPaymentAmount("");
                setBankName("");

                await Promise.all([
                    fetchCart(),
                    fetchCourses(),
                ]);

                await cayepSwal.fire({
                    icon: "success",
                    title:
                        "Course Plan Submitted",
                    text:
                        "Your selected courses have been submitted successfully for processing.",
                    confirmButtonText:
                        "Continue",
                });

                setCartOpen(false);
            } catch (error) {
                console.error(
                    "Submit plan error:",
                    error
                );

                const errorCode =
                    getErrorCode(error);

                await Promise.all([
                    fetchCart(),
                    fetchCourses(),
                ]);

                if (
                    errorCode ===
                    "ALREADY_ENROLLED"
                ) {
                    await cayepSwal.fire({
                        icon: "info",
                        title:
                            "Course Already Enrolled",
                        text:
                            "One or more selected courses are already enrolled for the current training year. Your planned courses have been refreshed.",
                    });

                    return;
                }

                if (
                    errorCode ===
                    "COURSE_FULL"
                ) {
                    await cayepSwal.fire({
                        icon: "warning",
                        title:
                            "Course Capacity Reached",
                        text:
                            "One of the selected courses has reached its capacity. Your planned courses have been refreshed.",
                    });

                    return;
                }

                await cayepSwal.fire({
                    icon: "error",
                    title:
                        "Submission Failed",
                    text: getErrorMessage(
                        error,
                        "Your course plan could not be submitted."
                    ),
                });
            } finally {
                setSubmitting(false);
            }
        };

    const cartTotal = useMemo(() => {
        return cart.reduce(
            (total, item) => {
                const course =
                    getCartCourse(
                        item
                    );

                const price =
                    course?.current_price ??
                    course?.price ??
                    item?.unit_price ??
                    0;

                return (
                    total +
                    Number(
                        price || 0
                    )
                );
            },
            0
        );
    }, [
        cart,
        getCartCourse,
    ]);

    const categories =
        useMemo(() => {
            const values =
                courses
                    .map(
                        (course) =>
                            course?.category
                    )
                    .filter(Boolean);

            return [
                ...new Set(
                    values
                ),
            ];
        }, [courses]);

    const levels =
        useMemo(() => {
            const values =
                courses
                    .map(
                        (course) =>
                            course?.level
                    )
                    .filter(Boolean);

            return [
                ...new Set(
                    values
                ),
            ];
        }, [courses]);

    const handlePreviousPage =
        () => {
            setPage(
                (current) =>
                    Math.max(
                        1,
                        current - 1
                    )
            );
        };

    const handleNextPage =
        () => {
            setPage(
                (current) =>
                    Math.min(
                        totalPages,
                        current + 1
                    )
            );
        };

    return (
        <div className="cayep-plan-page">
            <style>{`
                .cayep-plan-page {
                    min-height: 100vh;
                    background: #F4F7F9;
                    color: #142536;
                    padding: 24px;
                }

                .cayep-plan-container {
                    width: 100%;
                    max-width: 1440px;
                    margin: 0 auto;
                }

                .cayep-plan-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 20px;
                    margin-bottom: 22px;
                }

                .cayep-plan-heading {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }

                .cayep-plan-heading-icon {
                    width: 52px;
                    height: 52px;
                    border-radius: 14px;
                    background: #062A4B;
                    color: #F6C944;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 21px;
                    flex-shrink: 0;
                }

                .cayep-plan-heading h1 {
                    margin: 0;
                    font-size: 25px;
                    font-weight: 800;
                    color: #062A4B;
                }

                .cayep-plan-heading p {
                    margin: 4px 0 0;
                    color: #6B7280;
                    font-size: 14px;
                }

                .cayep-cart-toggle {
                    border: 0;
                    background: #062A4B;
                    color: #FFFFFF;
                    border-radius: 11px;
                    min-height: 46px;
                    padding: 0 17px;
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    cursor: pointer;
                    font-weight: 700;
                    box-shadow: 0 5px 15px rgba(6, 42, 75, 0.14);
                }

                .cayep-cart-toggle:hover {
                    background: #063A6B;
                }

                .cayep-cart-count {
                    min-width: 24px;
                    height: 24px;
                    padding: 0 6px;
                    border-radius: 20px;
                    background: #F6C944;
                    color: #062A4B;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: 800;
                }

                .cayep-eligibility {
                    border: 1px solid #E0E7EC;
                    background: #FFFFFF;
                    border-radius: 14px;
                    padding: 15px 17px;
                    margin-bottom: 18px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    box-shadow: 0 3px 12px rgba(20, 37, 54, 0.04);
                }

                .cayep-eligibility.success {
                    border-color: rgba(79, 174, 34, 0.35);
                    background: #F7FCF4;
                }

                .cayep-eligibility.warning {
                    border-color: rgba(192, 117, 13, 0.3);
                    background: #FFF9F0;
                }

                .cayep-eligibility-icon {
                    font-size: 19px;
                    color: #4FAE22;
                }

                .cayep-eligibility.warning .cayep-eligibility-icon {
                    color: #C0750D;
                }

                .cayep-eligibility-text strong {
                    display: block;
                    font-size: 14px;
                    color: #142536;
                }

                .cayep-eligibility-text span {
                    display: block;
                    margin-top: 2px;
                    color: #6B7280;
                    font-size: 13px;
                }

                .cayep-filters {
                    background: #FFFFFF;
                    border: 1px solid #E0E7EC;
                    border-radius: 14px;
                    padding: 14px;
                    margin-bottom: 20px;
                    display: grid;
                    grid-template-columns: minmax(220px, 1fr) 190px 170px;
                    gap: 10px;
                }

                .cayep-filter-input,
                .cayep-filter-select {
                    width: 100%;
                    height: 44px;
                    border: 1px solid #D7E0E6;
                    background: #FFFFFF;
                    border-radius: 9px;
                    padding: 0 13px;
                    color: #142536;
                    outline: none;
                    font-size: 14px;
                    box-sizing: border-box;
                }

                .cayep-filter-input:focus,
                .cayep-filter-select:focus {
                    border-color: #0F6DAB;
                    box-shadow: 0 0 0 3px rgba(15, 109, 171, 0.08);
                }

                .cayep-course-grid {
                    display: grid;
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    gap: 18px;
                }

                .cayep-course-card {
                    background: #FFFFFF;
                    border: 1px solid #E0E7EC;
                    border-radius: 15px;
                    overflow: hidden;
                    box-shadow: 0 5px 16px rgba(20, 37, 54, 0.045);
                    display: flex;
                    flex-direction: column;
                    min-width: 0;
                }

                .cayep-course-image {
                    height: 190px;
                    background: #E9EEF2;
                    position: relative;
                    overflow: hidden;
                }

                .cayep-course-image img {
                    width: 100%;
                    height: 100%;
                    display: block;
                    object-fit: cover;
                }

                .cayep-course-image-placeholder {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #062A4B;
                    font-size: 40px;
                }

                .cayep-course-category {
                    position: absolute;
                    left: 12px;
                    top: 12px;
                    background: rgba(6, 42, 75, 0.92);
                    color: #FFFFFF;
                    border-radius: 7px;
                    padding: 6px 9px;
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.25px;
                }

                .cayep-course-body {
                    padding: 16px;
                    display: flex;
                    flex-direction: column;
                    flex: 1;
                }

                .cayep-course-title {
                    margin: 0 0 8px;
                    font-size: 17px;
                    line-height: 1.35;
                    font-weight: 800;
                    color: #062A4B;
                }

                .cayep-course-description {
                    margin: 0 0 13px;
                    color: #6B7280;
                    font-size: 13px;
                    line-height: 1.55;
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                .cayep-course-meta {
                    display: flex;
                    flex-direction: column;
                    gap: 7px;
                    margin-bottom: 14px;
                }

                .cayep-course-meta-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: #6B7280;
                    font-size: 12px;
                }

                .cayep-course-meta-row svg {
                    color: #0F6DAB;
                    width: 13px;
                    flex-shrink: 0;
                }

                .cayep-course-slots {
                    border: 1px solid #E0E7EC;
                    background: #F8FAFB;
                    border-radius: 10px;
                    padding: 10px;
                    margin-bottom: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                }

                .cayep-course-slot-info {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    min-width: 0;
                }

                .cayep-course-slot-info > svg {
                    color: #0F6DAB;
                    flex-shrink: 0;
                    font-size: 16px;
                }

                .cayep-course-slot-text {
                    min-width: 0;
                }

                .cayep-course-slot-text strong {
                    display: block;
                    color: #142536;
                    font-size: 12px;
                    font-weight: 800;
                    line-height: 1.25;
                }

                .cayep-course-slot-text span {
                    display: block;
                    color: #6B7280;
                    font-size: 11px;
                    margin-top: 3px;
                }

                .cayep-course-drop-time {
                    margin-top: 6px;
                    color: #0F6DAB;
                    font-size: 10px;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                }

                .cayep-course-drop-time svg {
                    width: 10px;
                    color: #C0750D;
                    flex-shrink: 0;
                }

                .cayep-slot-badge {
                    flex-shrink: 0;
                    border-radius: 20px;
                    padding: 5px 8px;
                    background: #EAF6E4;
                    color: #3C851C;
                    font-size: 10px;
                    font-weight: 800;
                    white-space: nowrap;
                }

                .cayep-slot-badge.last {
                    background: #FFF4D6;
                    color: #A46A00;
                }

                .cayep-slot-badge.full {
                    background: #FDECEC;
                    color: #B42318;
                }

                .cayep-course-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    margin-top: auto;
                }

                .cayep-course-price {
                    color: #062A4B;
                    font-weight: 900;
                    font-size: 18px;
                    white-space: nowrap;
                }

                .cayep-add-btn {
                    border: 0;
                    min-height: 40px;
                    border-radius: 9px;
                    padding: 0 13px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 7px;
                    background: #062A4B;
                    color: #FFFFFF;
                    font-size: 12px;
                    font-weight: 800;
                    cursor: pointer;
                }

                .cayep-add-btn:hover:not(:disabled) {
                    background: #063A6B;
                }

                .cayep-add-btn.added {
                    background: #EAF6E4;
                    color: #3C851C;
                    cursor: default;
                }

                .cayep-add-btn.full {
                    background: #F1F3F5;
                    color: #8A929A;
                    cursor: not-allowed;
                }

                .cayep-add-btn:disabled {
                    opacity: 1;
                }

                .cayep-empty {
                    background: #FFFFFF;
                    border: 1px solid #E0E7EC;
                    border-radius: 15px;
                    padding: 55px 20px;
                    text-align: center;
                    color: #6B7280;
                    grid-column: 1 / -1;
                }

                .cayep-empty svg {
                    color: #C4C7C7;
                    font-size: 38px;
                    margin-bottom: 12px;
                }

                .cayep-empty strong {
                    display: block;
                    color: #142536;
                    font-size: 15px;
                    margin-bottom: 5px;
                }

                .cayep-loading {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 250px;
                    color: #062A4B;
                    grid-column: 1 / -1;
                    font-weight: 700;
                }

                .cayep-pagination {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 14px;
                    margin-top: 22px;
                }

                .cayep-pagination button {
                    border: 1px solid #D7E0E6;
                    background: #FFFFFF;
                    color: #062A4B;
                    border-radius: 8px;
                    min-height: 38px;
                    padding: 0 13px;
                    cursor: pointer;
                    font-weight: 700;
                }

                .cayep-pagination button:disabled {
                    color: #A3ABB2;
                    cursor: not-allowed;
                    background: #F4F6F7;
                }

                .cayep-pagination span {
                    color: #6B7280;
                    font-size: 13px;
                    font-weight: 700;
                }

                .cayep-cart-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(6, 15, 22, 0.42);
                    z-index: 1000;
                }

                .cayep-cart-panel {
                    position: fixed;
                    top: 0;
                    right: 0;
                    width: min(440px, 100%);
                    height: 100vh;
                    background: #FFFFFF;
                    z-index: 1001;
                    box-shadow: -10px 0 35px rgba(6, 42, 75, 0.16);
                    display: flex;
                    flex-direction: column;
                }

                .cayep-cart-header {
                    padding: 18px;
                    background: #062A4B;
                    color: #FFFFFF;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                }

                .cayep-cart-header h2 {
                    margin: 0;
                    font-size: 18px;
                }

                .cayep-cart-header p {
                    margin: 4px 0 0;
                    color: rgba(255,255,255,0.72);
                    font-size: 11px;
                }

                .cayep-cart-header button {
                    border: 0;
                    background: transparent;
                    color: #FFFFFF;
                    font-size: 23px;
                    cursor: pointer;
                    line-height: 1;
                }

                .cayep-cart-content {
                    flex: 1;
                    overflow-y: auto;
                    padding: 16px;
                }

                .cayep-cart-item {
                    border: 1px solid #E0E7EC;
                    border-radius: 11px;
                    padding: 10px;
                    display: flex;
                    gap: 10px;
                    margin-bottom: 10px;
                }

                .cayep-cart-thumb {
                    width: 62px;
                    height: 62px;
                    border-radius: 8px;
                    background: #EEF2F4;
                    overflow: hidden;
                    flex-shrink: 0;
                }

                .cayep-cart-thumb img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .cayep-cart-thumb-placeholder {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #062A4B;
                }

                .cayep-cart-item-info {
                    min-width: 0;
                    flex: 1;
                }

                .cayep-cart-item-title {
                    color: #062A4B;
                    font-size: 13px;
                    font-weight: 800;
                    margin: 0 0 3px;
                    line-height: 1.35;
                }

                .cayep-cart-item-facilitator {
                    color: #6B7280;
                    font-size: 11px;
                    margin-bottom: 6px;
                }

                .cayep-cart-slot {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    color: #3C851C;
                    background: #F1F9ED;
                    border-radius: 6px;
                    padding: 4px 6px;
                    font-size: 10px;
                    font-weight: 800;
                    margin-bottom: 6px;
                }

                .cayep-cart-slot.last {
                    color: #A46A00;
                    background: #FFF7E2;
                }

                .cayep-cart-slot.full {
                    color: #B42318;
                    background: #FDECEC;
                }

                .cayep-cart-capacity {
                    color: #6B7280;
                    font-size: 10px;
                    margin-left: 4px;
                }

                .cayep-cart-item-bottom {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;
                }

                .cayep-cart-item-price {
                    color: #142536;
                    font-size: 13px;
                    font-weight: 900;
                }

                .cayep-cart-remove {
                    width: 31px;
                    height: 31px;
                    border: 0;
                    border-radius: 7px;
                    background: #FDECEC;
                    color: #B42318;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .cayep-cart-empty {
                    padding: 45px 15px;
                    text-align: center;
                    color: #6B7280;
                }

                .cayep-cart-empty svg {
                    font-size: 36px;
                    color: #C4C7C7;
                    margin-bottom: 10px;
                }

                .cayep-payment {
                    border-top: 1px solid #E0E7EC;
                    padding: 16px;
                    background: #FAFBFC;
                }

                .cayep-payment-title {
                    margin: 0 0 11px;
                    color: #062A4B;
                    font-size: 14px;
                    font-weight: 900;
                }

                .cayep-payment-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 9px;
                }

                .cayep-payment-grid .full {
                    grid-column: 1 / -1;
                }

                .cayep-payment label {
                    display: block;
                    color: #6B7280;
                    font-size: 10px;
                    font-weight: 800;
                    margin-bottom: 4px;
                    text-transform: uppercase;
                }

                .cayep-payment input,
                .cayep-payment select {
                    width: 100%;
                    height: 39px;
                    border: 1px solid #D7E0E6;
                    border-radius: 8px;
                    padding: 0 9px;
                    box-sizing: border-box;
                    background: #FFFFFF;
                    color: #142536;
                    outline: none;
                    font-size: 12px;
                }

                .cayep-payment input:focus,
                .cayep-payment select:focus {
                    border-color: #0F6DAB;
                }

                .cayep-cart-total {
                    margin-top: 13px;
                    padding-top: 12px;
                    border-top: 1px solid #E0E7EC;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .cayep-cart-total span {
                    color: #6B7280;
                    font-size: 13px;
                    font-weight: 700;
                }

                .cayep-cart-total strong {
                    color: #062A4B;
                    font-size: 19px;
                }

                .cayep-submit-plan {
                    width: 100%;
                    min-height: 45px;
                    margin-top: 12px;
                    border: 0;
                    border-radius: 9px;
                    background: #062A4B;
                    color: #FFFFFF;
                    cursor: pointer;
                    font-size: 13px;
                    font-weight: 900;
                }

                .cayep-submit-plan:hover:not(:disabled) {
                    background: #063A6B;
                }

                .cayep-submit-plan:disabled {
                    opacity: 0.55;
                    cursor: not-allowed;
                }

                @media (max-width: 1100px) {
                    .cayep-course-grid {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }
                }

                @media (max-width: 760px) {
                    .cayep-plan-page {
                        padding: 15px;
                    }

                    .cayep-plan-header {
                        align-items: flex-start;
                    }

                    .cayep-plan-heading h1 {
                        font-size: 21px;
                    }

                    .cayep-filters {
                        grid-template-columns: 1fr;
                    }

                    .cayep-course-grid {
                        grid-template-columns: 1fr;
                    }
                }

                @media (max-width: 520px) {
                    .cayep-plan-header {
                        flex-direction: column;
                    }

                    .cayep-cart-toggle {
                        width: 100%;
                        justify-content: center;
                    }

                    .cayep-course-image {
                        height: 175px;
                    }

                    .cayep-course-slots {
                        align-items: flex-start;
                    }

                    .cayep-course-footer {
                        align-items: flex-end;
                    }

                    .cayep-payment-grid {
                        grid-template-columns: 1fr;
                    }

                    .cayep-payment-grid .full {
                        grid-column: auto;
                    }
                }
            `}</style>

            <div className="cayep-plan-container">
                <div className="cayep-plan-header">
                    <div className="cayep-plan-heading">
                        <div className="cayep-plan-heading-icon">
                            <FaGraduationCap />
                        </div>

                        <div>
                            <h1>
                                Plan Your Courses
                            </h1>

                            <p>
                                Select one or more courses
                                you want to take for your
                                current training year.
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="cayep-cart-toggle"
                        onClick={() =>
                            setCartOpen(true)
                        }
                    >
                        <FaShoppingCart />

                        Planned Courses

                        <span className="cayep-cart-count">
                            {cart.length}
                        </span>
                    </button>
                </div>

                {!eligibilityLoading && (
                    <div
                        className={`cayep-eligibility ${
                            isEligible
                                ? "success"
                                : "warning"
                        }`}
                    >
                        {isEligible ? (
                            <FaCheckCircle className="cayep-eligibility-icon" />
                        ) : (
                            <FaExclamationTriangle className="cayep-eligibility-icon" />
                        )}

                        <div className="cayep-eligibility-text">
                            <strong>
                                {isEligible
                                    ? "You are eligible to plan courses"
                                    : "Course planning is currently unavailable"}
                            </strong>

                            <span>
                                {eligibility?.message ||
                                    (isEligible
                                        ? "You can select one or more courses from the catalog below."
                                        : "Please complete the required eligibility conditions before planning courses.")}
                            </span>
                        </div>
                    </div>
                )}

                <div className="cayep-filters">
                    <input
                        type="search"
                        className="cayep-filter-input"
                        placeholder="Search courses..."
                        value={search}
                        onChange={(event) =>
                            setSearch(
                                event.target.value
                            )
                        }
                    />

                    <select
                        className="cayep-filter-select"
                        value={category}
                        onChange={(event) =>
                            setCategory(
                                event.target.value
                            )
                        }
                    >
                        <option value="">
                            All Categories
                        </option>

                        {categories.map(
                            (item) => (
                                <option
                                    key={item}
                                    value={item}
                                >
                                    {item}
                                </option>
                            )
                        )}
                    </select>

                    <select
                        className="cayep-filter-select"
                        value={level}
                        onChange={(event) =>
                            setLevel(
                                event.target.value
                            )
                        }
                    >
                        <option value="">
                            All Levels
                        </option>

                        {levels.map(
                            (item) => (
                                <option
                                    key={item}
                                    value={item}
                                >
                                    {item}
                                </option>
                            )
                        )}
                    </select>
                </div>

                <div className="cayep-course-grid">
                    {loading ? (
                        <div className="cayep-loading">
                            Loading courses...
                        </div>
                    ) : courses.length === 0 ? (
                        <div className="cayep-empty">
                            <FaBookOpen />

                            <strong>
                                No courses found
                            </strong>

                            <span>
                                Try changing your
                                search or filter
                                options.
                            </span>
                        </div>
                    ) : (
                        courses.map(
                            (course) => {
                                const slotState =
                                    getSlotState(
                                        course
                                    );

                                const inCart =
                                    isInCart(
                                        course.id
                                    );

                                return (
                                    <div
                                        className="cayep-course-card"
                                        key={
                                            course.id
                                        }
                                    >
                                        <div className="cayep-course-image">
                                            {course.thumbnail ||
                                            course.image ? (
                                                <img
                                                    src={
                                                        course.thumbnail ||
                                                        course.image
                                                    }
                                                    alt={
                                                        course.course_title ||
                                                        course.name
                                                    }
                                                />
                                            ) : (
                                                <div className="cayep-course-image-placeholder">
                                                    <FaBookOpen />
                                                </div>
                                            )}

                                            {course.category && (
                                                <span className="cayep-course-category">
                                                    {
                                                        course.category
                                                    }
                                                </span>
                                            )}
                                        </div>

                                        <div className="cayep-course-body">
                                            <h2 className="cayep-course-title">
                                                {course.course_title ||
                                                    course.name}
                                            </h2>

                                            <p className="cayep-course-description">
                                                {course.description ||
                                                    "Professional training course designed to develop practical knowledge and leadership skills."}
                                            </p>

                                            <div className="cayep-course-meta">
                                                {course.facilitator && (
                                                    <div className="cayep-course-meta-row">
                                                        <FaUserTie />

                                                        <span>
                                                            {
                                                                course.facilitator
                                                            }
                                                        </span>
                                                    </div>
                                                )}

                                                {course.level && (
                                                    <div className="cayep-course-meta-row">
                                                        <FaGraduationCap />

                                                        <span>
                                                            {
                                                                course.level
                                                            }
                                                        </span>
                                                    </div>
                                                )}

                                                {course.duration && (
                                                    <div className="cayep-course-meta-row">
                                                        <FaClock />

                                                        <span>
                                                            {
                                                                course.duration
                                                            }
                                                        </span>
                                                    </div>
                                                )}

                                                {course.video_url && (
                                                    <div className="cayep-course-meta-row">
                                                        <FaVideo />

                                                        <span>
                                                            Video learning available
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="cayep-course-slots">
                                                <div className="cayep-course-slot-info">
                                                    {slotState.full ? (
                                                        <FaExclamationTriangle />
                                                    ) : (
                                                        <FaUsers />
                                                    )}

                                                    <div className="cayep-course-slot-text">
                                                        <strong>
                                                            {slotState.full
                                                                ? "Course Capacity"
                                                                : slotState.unlimited
                                                                ? "Course Capacity"
                                                                : `${slotState.remaining} ${
                                                                      slotState.remaining ===
                                                                      1
                                                                          ? "slot"
                                                                          : "slots"
                                                                  } available`}
                                                        </strong>

                                                        <span>
                                                            {slotState.unlimited
                                                                ? "Open enrollment"
                                                                : `${slotState.taken} of ${slotState.total} ${
                                                                      slotState.total ===
                                                                      1
                                                                          ? "seat"
                                                                          : "seats"
                                                                  } occupied`}
                                                        </span>

                                                        {course.latest_drop_ago && (
                                                            <div className="cayep-course-drop-time">
                                                                <FaClock />

                                                                <span>
                                                                    Seat released{" "}
                                                                    {
                                                                        course.latest_drop_ago
                                                                    }
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <span
                                                    className={`cayep-slot-badge ${
                                                        slotState.full
                                                            ? "full"
                                                            : slotState.lastSlot
                                                            ? "last"
                                                            : ""
                                                    }`}
                                                >
                                                    {getSlotLabel(
                                                        course
                                                    )}
                                                </span>
                                            </div>

                                            <div className="cayep-course-footer">
                                                <div className="cayep-course-price">
                                                    {course.current_price ||
                                                    course.price
                                                        ? `USD ${formatMoney(
                                                              course.current_price ??
                                                                  course.price
                                                          )}`
                                                        : "Free"}
                                                </div>

                                                <button
                                                    type="button"
                                                    className={`cayep-add-btn ${
                                                        inCart
                                                            ? "added"
                                                            : slotState.full
                                                            ? "full"
                                                            : ""
                                                    }`}
                                                    disabled={
                                                        inCart ||
                                                        slotState.full ||
                                                        !isEligible ||
                                                        cartLoading ||
                                                        refreshingSlots
                                                    }
                                                    onClick={() =>
                                                        addToCart(
                                                            course
                                                        )
                                                    }
                                                >
                                                    {inCart ? (
                                                        <>
                                                            <FaCheckCircle />
                                                            Planned
                                                        </>
                                                    ) : slotState.full ? (
                                                        <>
                                                            <FaExclamationTriangle />
                                                            Class Full
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaPlus />
                                                            Add Course
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                        )
                    )}
                </div>

                {!loading &&
                    courses.length > 0 && (
                        <div className="cayep-pagination">
                            <button
                                type="button"
                                disabled={
                                    page <= 1
                                }
                                onClick={
                                    handlePreviousPage
                                }
                            >
                                Previous
                            </button>

                            <span>
                                Page {page} of{" "}
                                {totalPages}
                            </span>

                            <button
                                type="button"
                                disabled={
                                    page >=
                                    totalPages
                                }
                                onClick={
                                    handleNextPage
                                }
                            >
                                Next
                            </button>
                        </div>
                    )}
            </div>

            {cartOpen && (
                <>
                    <div
                        className="cayep-cart-overlay"
                        onClick={() =>
                            setCartOpen(false)
                        }
                    />

                    <aside className="cayep-cart-panel">
                        <div className="cayep-cart-header">
                            <div>
                                <h2>
                                    Planned Courses
                                </h2>

                                <p>
                                    {cart.length} course
                                    {cart.length === 1
                                        ? ""
                                        : "s"} selected
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    setCartOpen(
                                        false
                                    )
                                }
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>

                        <div className="cayep-cart-content">
                            {cartLoading ? (
                                <div className="cayep-cart-empty">
                                    Loading planned
                                    courses...
                                </div>
                            ) : cart.length === 0 ? (
                                <div className="cayep-cart-empty">
                                    <FaShoppingCart />

                                    <div>
                                        You have not
                                        planned any
                                        courses yet.
                                    </div>
                                </div>
                            ) : (
                                cart.map(
                                    (item) => {
                                        const course =
                                            getCartCourse(
                                                item
                                            );

                                        const planId =
                                            getPlanId(
                                                item
                                            );

                                        const slotState =
                                            getSlotState(
                                                course
                                            );

                                        return (
                                            <div
                                                className="cayep-cart-item"
                                                key={
                                                    planId ||
                                                    course?.id
                                                }
                                            >
                                                <div className="cayep-cart-thumb">
                                                    {course?.thumbnail ||
                                                    course?.image ? (
                                                        <img
                                                            src={
                                                                course.thumbnail ||
                                                                course.image
                                                            }
                                                            alt={
                                                                course.course_title ||
                                                                course.name
                                                            }
                                                        />
                                                    ) : (
                                                        <div className="cayep-cart-thumb-placeholder">
                                                            <FaBookOpen />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="cayep-cart-item-info">
                                                    <h3 className="cayep-cart-item-title">
                                                        {course?.course_title ||
                                                            course?.name}
                                                    </h3>

                                                    {course?.facilitator && (
                                                        <div className="cayep-cart-item-facilitator">
                                                            {
                                                                course.facilitator
                                                            }
                                                        </div>
                                                    )}

                                                    <div
                                                        className={`cayep-cart-slot ${
                                                            slotState.full
                                                                ? "full"
                                                                : slotState.lastSlot
                                                                ? "last"
                                                                : ""
                                                        }`}
                                                    >
                                                        {slotState.full ? (
                                                            <FaExclamationTriangle />
                                                        ) : (
                                                            <FaUsers />
                                                        )}

                                                        <span>
                                                            {slotState.full
                                                                ? "Class is currently full"
                                                                : slotState.unlimited
                                                                ? "Open enrollment"
                                                                : `${slotState.remaining} ${
                                                                      slotState.remaining ===
                                                                      1
                                                                          ? "slot"
                                                                          : "slots"
                                                                  } available`}
                                                        </span>

                                                        {!slotState.unlimited && (
                                                            <span className="cayep-cart-capacity">
                                                                {
                                                                    slotState.taken
                                                                }
                                                                /
                                                                {
                                                                    slotState.total
                                                                }
                                                            </span>
                                                        )}
                                                    </div>

                                                    {course?.latest_drop_ago && (
                                                        <div className="cayep-course-drop-time">
                                                            <FaClock />

                                                            <span>
                                                                Seat released{" "}
                                                                {
                                                                    course.latest_drop_ago
                                                                }
                                                            </span>
                                                        </div>
                                                    )}

                                                    <div className="cayep-cart-item-bottom">
                                                        <span className="cayep-cart-item-price">
                                                            {course?.current_price ||
                                                            course?.price
                                                                ? `USD ${formatMoney(
                                                                      course.current_price ??
                                                                          course.price
                                                                  )}`
                                                                : "Free"}
                                                        </span>

                                                        <button
                                                            type="button"
                                                            className="cayep-cart-remove"
                                                            disabled={
                                                                cartLoading ||
                                                                !planId
                                                            }
                                                            onClick={() =>
                                                                removeFromCart(
                                                                    planId
                                                                )
                                                            }
                                                            title="Remove course"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }
                                )
                            )}
                        </div>

                        {cart.length > 0 && (
                            <div className="cayep-payment">
                                <h3 className="cayep-payment-title">
                                    Payment Information
                                </h3>

                                <div className="cayep-payment-grid">
                                    <div>
                                        <label>
                                            Payment
                                            Method
                                        </label>

                                        <select
                                            value={
                                                paymentMethod
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                setPaymentMethod(
                                                    event
                                                        .target
                                                        .value
                                                )
                                            }
                                        >
                                            <option value="bank_deposit">
                                                Bank
                                                Deposit
                                            </option>

                                            <option value="cash">
                                                Cash
                                            </option>

                                            <option value="mobile_money">
                                                Mobile
                                                Money
                                            </option>
                                        </select>
                                    </div>

                                    {paymentMethod ===
                                        "bank_deposit" && (
                                        <div>
                                            <label>
                                                Bank
                                            </label>

                                            <select
                                                value={
                                                    bankName
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    setBankName(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                            >
                                                <option value="">
                                                    Select
                                                    Bank
                                                </option>

                                                {BANKS.map(
                                                    (
                                                        bank
                                                    ) => (
                                                        <option
                                                            key={
                                                                bank
                                                            }
                                                            value={
                                                                bank
                                                            }
                                                        >
                                                            {
                                                                bank
                                                            }
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </div>
                                    )}

                                    <div>
                                        <label>
                                            Payment
                                            Amount
                                        </label>

                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={
                                                paymentAmount
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                setPaymentAmount(
                                                    event
                                                        .target
                                                        .value
                                                )
                                            }
                                            placeholder="0.00"
                                        />
                                    </div>

                                    <div>
                                        <label>
                                            Transaction
                                            Reference
                                        </label>

                                        <input
                                            type="text"
                                            value={
                                                transactionReference
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                setTransactionReference(
                                                    event
                                                        .target
                                                        .value
                                                )
                                            }
                                            placeholder="Transaction reference"
                                        />
                                    </div>
                                </div>

                                <div className="cayep-cart-total">
                                    <span>
                                        {cart.length} Course
                                        {cart.length === 1
                                            ? ""
                                            : "s"} Total
                                    </span>

                                    <strong>
                                        USD{" "}
                                        {formatMoney(
                                            cartTotal
                                        )}
                                    </strong>
                                </div>

                                <button
                                    type="button"
                                    className="cayep-submit-plan"
                                    disabled={
                                        submitting ||
                                        cartLoading ||
                                        refreshingSlots
                                    }
                                    onClick={
                                        submitPlan
                                    }
                                >
                                    {submitting
                                        ? "Submitting..."
                                        : `Submit ${cart.length} Course${
                                              cart.length ===
                                              1
                                                  ? ""
                                                  : "s"
                                          }`}
                                </button>
                            </div>
                        )}
                    </aside>
                </>
            )}
        </div>
    );
};

export default PlanCoursesPage;






// import React, {
//     useCallback,
//     useEffect,
//     useMemo,
//     useState,
// } from "react";
// import Swal from "sweetalert2";

// import {
//     FaBookOpen,
//     FaCheckCircle,
//     FaClock,
//     FaExclamationTriangle,
//     FaGraduationCap,
//     FaPlus,
//     FaShoppingCart,
//     FaTrash,
//     FaUsers,
//     FaUserTie,
//     FaVideo,
// } from "react-icons/fa";

// import api from "../../api/api";
// import { useAcademicYear } from "../../AcademicYearContext";

// const formatMoney = (value) => {
//     const amount = Number(value || 0);

//     return new Intl.NumberFormat("en-US", {
//         minimumFractionDigits: 2,
//         maximumFractionDigits: 2,
//     }).format(amount);
// };

// const getErrorMessage = (
//     error,
//     fallback = "Something went wrong."
// ) => {
//     const data = error?.response?.data;

//     if (!data) {
//         return error?.message || fallback;
//     }

//     if (typeof data === "string") {
//         return data;
//     }

//     if (data.detail) {
//         return data.detail;
//     }

//     if (data.message) {
//         return data.message;
//     }

//     if (data.error) {
//         return data.error;
//     }

//     if (data.non_field_errors?.length) {
//         return data.non_field_errors[0];
//     }

//     const firstKey = Object.keys(data)[0];

//     if (firstKey) {
//         const value = data[firstKey];

//         if (Array.isArray(value)) {
//             return value[0];
//         }

//         if (typeof value === "string") {
//             return value;
//         }

//         if (
//             value &&
//             typeof value === "object"
//         ) {
//             if (value.detail) {
//                 return value.detail;
//             }

//             if (value.message) {
//                 return value.message;
//             }
//         }
//     }

//     return fallback;
// };

// const getErrorCode = (error) => {
//     const data = error?.response?.data;

//     if (!data || typeof data !== "object") {
//         return null;
//     }

//     return (
//         data.code ||
//         data.error_code ||
//         data.errorCode ||
//         null
//     );
// };

// const cayepSwal = Swal.mixin({
//     confirmButtonColor: "#062A4B",
//     cancelButtonColor: "#6B7280",
//     background: "#FFFFFF",
//     color: "#142536",
// });

// const BANKS = [
//     "LBDI",
//     "Ecobank Liberia",
//     "International Bank Liberia",
//     "Bloom Bank Africa",
//     "Sapelle International Bank",
//     "UBA Liberia",
//     "Access Bank Liberia",
//     "GTBank Liberia",
//     "Afriland First Bank Liberia",
// ];

// const getSlotState = (course) => {
//     const rawTotal = Number(
//         course?.slots ?? 0
//     );

//     const rawTaken = Number(
//         course?.slots_taken ?? 0
//     );

//     const total = Number.isFinite(rawTotal)
//         ? Math.max(0, rawTotal)
//         : 0;

//     const taken = Number.isFinite(rawTaken)
//         ? Math.max(0, rawTaken)
//         : 0;

//     const unlimited =
//         total === 0 &&
//         course?.is_full !== true;

//     const rawRemaining =
//         course?.slots_remaining;

//     const calculatedRemaining =
//         Math.max(
//             total - taken,
//             0
//         );

//     const remaining = unlimited
//         ? null
//         : Math.max(
//               0,
//               Number.isFinite(
//                   Number(rawRemaining)
//               )
//                   ? Number(rawRemaining)
//                   : calculatedRemaining
//           );

//     const full =
//         !unlimited &&
//         (
//             course?.is_full === true ||
//             remaining <= 0
//         );

//     const lastSlot =
//         !unlimited &&
//         !full &&
//         remaining === 1;

//     return {
//         total,
//         taken,
//         remaining,
//         full,
//         lastSlot,
//         unlimited,
//     };
// };

// const getSlotLabel = (course) => {
//     const slotState =
//         getSlotState(course);

//     if (slotState.full) {
//         return "Class Full";
//     }

//     if (slotState.unlimited) {
//         return "Open";
//     }

//     if (slotState.lastSlot) {
//         return "Last Slot";
//     }

//     return `${slotState.remaining} Available`;
// };

// const getCourseFromPlan = (item) => {
//     if (!item) {
//         return null;
//     }

//     return (
//         item.course ||
//         item.subject ||
//         null
//     );
// };

// const getPlanIdFromItem = (item) => {
//     if (!item) {
//         return null;
//     }

//     return (
//         item.id ||
//         item.plan_id ||
//         item.course_plan_id ||
//         null
//     );
// };

// const getCourseIdFromPlan = (item) => {
//     const course =
//         getCourseFromPlan(item);

//     if (course?.id != null) {
//         return Number(course.id);
//     }

//     if (item?.course_id != null) {
//         return Number(item.course_id);
//     }

//     if (item?.subject_id != null) {
//         return Number(item.subject_id);
//     }

//     return null;
// };

// const normalizePlanItems = (items) => {
//     if (!Array.isArray(items)) {
//         return [];
//     }

//     const seenPlanIds = new Set();
//     const seenCourseIds = new Set();

//     return items.filter((item) => {
//         const planId =
//             getPlanIdFromItem(item);

//         const courseId =
//             getCourseIdFromPlan(item);

//         if (
//             planId != null &&
//             seenPlanIds.has(planId)
//         ) {
//             return false;
//         }

//         if (
//             courseId != null &&
//             seenCourseIds.has(courseId)
//         ) {
//             return false;
//         }

//         if (planId != null) {
//             seenPlanIds.add(planId);
//         }

//         if (courseId != null) {
//             seenCourseIds.add(courseId);
//         }

//         return true;
//     });
// };

// const PlanCoursesPage = () => {
//     const { selectedYear } =
//         useAcademicYear();

//     const [courses, setCourses] =
//         useState([]);

//     const [cart, setCart] =
//         useState([]);

//     const [loading, setLoading] =
//         useState(true);

//     const [cartLoading, setCartLoading] =
//         useState(false);

//     const [cartOpen, setCartOpen] =
//         useState(false);

//     const [search, setSearch] =
//         useState("");

//     const [category, setCategory] =
//         useState("");

//     const [level, setLevel] =
//         useState("");

//     const [page, setPage] =
//         useState(1);

//     const [pageSize] =
//         useState(12);

//     const [totalPages, setTotalPages] =
//         useState(1);

//     const [
//         paymentMethod,
//         setPaymentMethod,
//     ] = useState("bank_deposit");

//     const [bankName, setBankName] =
//         useState("");

//     const [
//         transactionReference,
//         setTransactionReference,
//     ] = useState("");

//     const [paymentAmount, setPaymentAmount] =
//         useState("");

//     const [submitting, setSubmitting] =
//         useState(false);

//     const [eligibility, setEligibility] =
//         useState(null);

//     const [
//         eligibilityLoading,
//         setEligibilityLoading,
//     ] = useState(true);

//     const [
//         refreshingSlots,
//         setRefreshingSlots,
//     ] = useState(false);

//     const fetchEligibility =
//         useCallback(async () => {
//             setEligibilityLoading(true);

//             try {
//                 const response =
//                     await api.get(
//                         "/api/course-learning/eligibility/status/"
//                     );

//                 setEligibility(
//                     response.data
//                 );
//             } catch (error) {
//                 console.error(
//                     "Eligibility error:",
//                     error
//                 );

//                 setEligibility({
//                     eligible: false,
//                     message:
//                         getErrorMessage(
//                             error,
//                             "Unable to verify course eligibility."
//                         ),
//                 });
//             } finally {
//                 setEligibilityLoading(
//                     false
//                 );
//             }
//         }, []);

//     const fetchCart =
//         useCallback(async () => {
//             setCartLoading(true);

//             try {
//                 const response =
//                     await api.get(
//                         "/api/course-learning/plans/"
//                     );

//                 const data =
//                     response.data;

//                 const items =
//                     Array.isArray(data)
//                         ? data
//                         : data?.items ||
//                           data?.results ||
//                           [];

//                 setCart(
//                     normalizePlanItems(
//                         items
//                     )
//                 );
//             } catch (error) {
//                 console.error(
//                     "Cart error:",
//                     error
//                 );

//                 setCart([]);
//             } finally {
//                 setCartLoading(false);
//             }
//         }, []);

//     const fetchCourses =
//         useCallback(async () => {
//             setLoading(true);

//             try {
//                 const params = {
//                     page,
//                     page_size: pageSize,
//                 };

//                 if (search.trim()) {
//                     params.search =
//                         search.trim();
//                 }

//                 if (category) {
//                     params.category =
//                         category;
//                 }

//                 if (level) {
//                     params.level =
//                         level;
//                 }

//                 const response =
//                     await api.get(
//                         "/api/course-learning/courses/",
//                         {
//                             params,
//                         }
//                     );

//                 const data =
//                     response.data;

//                 if (Array.isArray(data)) {
//                     setCourses(data);
//                     setTotalPages(1);
//                     return;
//                 }

//                 const results =
//                     data?.results ||
//                     data?.items ||
//                     [];

//                 setCourses(results);

//                 const count =
//                     Number(
//                         data?.count || 0
//                     );

//                 if (count > 0) {
//                     setTotalPages(
//                         Math.max(
//                             1,
//                             Math.ceil(
//                                 count /
//                                     pageSize
//                             )
//                         )
//                     );
//                 } else {
//                     setTotalPages(
//                         results.length > 0
//                             ? Math.max(
//                                   1,
//                                   page
//                               )
//                             : 1
//                     );
//                 }
//             } catch (error) {
//                 console.error(
//                     "Courses error:",
//                     error
//                 );

//                 setCourses([]);
//                 setTotalPages(1);

//                 await cayepSwal.fire({
//                     icon: "error",
//                     title:
//                         "Unable to Load Courses",
//                     text: getErrorMessage(
//                         error,
//                         "The course catalog could not be loaded."
//                     ),
//                 });
//             } finally {
//                 setLoading(false);
//             }
//         }, [
//             category,
//             level,
//             page,
//             pageSize,
//             search,
//         ]);

//     const refreshCourseData =
//         useCallback(async () => {
//             setRefreshingSlots(true);

//             try {
//                 await Promise.all([
//                     fetchCourses(),
//                     fetchCart(),
//                 ]);
//             } finally {
//                 setRefreshingSlots(false);
//             }
//         }, [
//             fetchCourses,
//             fetchCart,
//         ]);

//     useEffect(() => {
//         fetchEligibility();
//     }, [fetchEligibility]);

//     useEffect(() => {
//         fetchCart();
//     }, [
//         fetchCart,
//         selectedYear,
//     ]);

//     useEffect(() => {
//         fetchCourses();
//     }, [
//         fetchCourses,
//         selectedYear,
//     ]);

//     useEffect(() => {
//         setPage(1);
//     }, [
//         search,
//         category,
//         level,
//     ]);

//     useEffect(() => {
//         const refreshTimer =
//             window.setInterval(() => {
//                 refreshCourseData();
//             }, 60000);

//         return () => {
//             window.clearInterval(
//                 refreshTimer
//             );
//         };
//     }, [refreshCourseData]);

//     const isEligible = useMemo(() => {
//         if (!eligibility) {
//             return false;
//         }

//         return Boolean(
//             eligibility.eligible ??
//                 eligibility.is_eligible ??
//                 eligibility.can_plan_courses ??
//                 eligibility.can_plan
//         );
//     }, [eligibility]);

//     const isInCart =
//         useCallback(
//             (courseId) => {
//                 const normalizedCourseId =
//                     Number(courseId);

//                 if (
//                     !Number.isFinite(
//                         normalizedCourseId
//                     )
//                 ) {
//                     return false;
//                 }

//                 return cart.some(
//                     (item) =>
//                         getCourseIdFromPlan(
//                             item
//                         ) ===
//                         normalizedCourseId
//                 );
//             },
//             [cart]
//         );

//     const getCartCourse =
//         useCallback((item) => {
//             return (
//                 getCourseFromPlan(
//                     item
//                 ) || item
//             );
//         }, []);

//     const getPlanId =
//         useCallback((item) => {
//             return getPlanIdFromItem(
//                 item
//             );
//         }, []);

//     const addToCart = async (
//         course
//     ) => {
//         if (!isEligible) {
//             await cayepSwal.fire({
//                 icon: "warning",
//                 title: "Not Eligible",
//                 text:
//                     eligibility?.message ||
//                     "You are not currently eligible to plan courses.",
//             });

//             return;
//         }

//         const slotState =
//             getSlotState(course);

//         if (slotState.full) {
//             await cayepSwal.fire({
//                 icon: "warning",
//                 title: "Class Full",
//                 text:
//                     "This course has reached its maximum capacity.",
//             });

//             await fetchCourses();

//             return;
//         }

//         if (isInCart(course.id)) {
//             await cayepSwal.fire({
//                 icon: "info",
//                 title: "Already Planned",
//                 text:
//                     "This course is already in your planned courses.",
//             });

//             return;
//         }

//         try {
//             setCartLoading(true);

//             await api.post(
//                 "/api/course-learning/plans/add/",
//                 {
//                     course_id:
//                         course.id,
//                 }
//             );

//             await Promise.all([
//                 fetchCart(),
//                 fetchCourses(),
//             ]);

//             await cayepSwal.fire({
//                 icon: "success",
//                 title: "Course Added",
//                 text: `${
//                     course.course_title ||
//                     course.name
//                 } has been added to your planned courses.`,
//                 timer: 1800,
//                 showConfirmButton: false,
//             });
//         } catch (error) {
//             console.error(
//                 "Add course error:",
//                 error
//             );

//             const errorCode =
//                 getErrorCode(error);

//             const errorText =
//                 String(
//                     getErrorMessage(
//                         error,
//                         ""
//                     )
//                 ).toLowerCase();

//             await Promise.all([
//                 fetchCourses(),
//                 fetchCart(),
//             ]);

//             if (
//                 errorCode ===
//                     "COURSE_FULL" ||
//                 errorCode ===
//                     "NO_SLOTS" ||
//                 errorText.includes(
//                     "capacity"
//                 ) ||
//                 errorText.includes(
//                     "class full"
//                 )
//             ) {
//                 await cayepSwal.fire({
//                     icon: "warning",
//                     title:
//                         "Class Full",
//                     text: getErrorMessage(
//                         error,
//                         "This course has just reached its maximum capacity."
//                     ),
//                 });

//                 return;
//             }

//             if (
//                 errorCode ===
//                 "ALREADY_ENROLLED"
//             ) {
//                 await cayepSwal.fire({
//                     icon: "info",
//                     title:
//                         "Already Enrolled",
//                     text:
//                         "You are already enrolled in this course for the current training year.",
//                 });

//                 return;
//             }

//             if (
//                 errorCode ===
//                 "COURSE_COMPLETED"
//             ) {
//                 await cayepSwal.fire({
//                     icon: "info",
//                     title:
//                         "Course Completed",
//                     text:
//                         "You have already completed this course and cannot plan it again.",
//                 });

//                 return;
//             }

//             if (
//                 errorCode ===
//                 "ALREADY_PLANNED"
//             ) {
//                 await fetchCart();

//                 await cayepSwal.fire({
//                     icon: "info",
//                     title:
//                         "Already Planned",
//                     text:
//                         "This course is already in your planned courses.",
//                 });

//                 return;
//             }

//             await cayepSwal.fire({
//                 icon: "error",
//                 title:
//                     "Unable to Add Course",
//                 text: getErrorMessage(
//                     error,
//                     "The course could not be added to your planned courses."
//                 ),
//             });
//         } finally {
//             setCartLoading(false);
//         }
//     };

//     const removeFromCart =
//         async (planId) => {
//             if (!planId) {
//                 await cayepSwal.fire({
//                     icon: "error",
//                     title:
//                         "Invalid Course Plan",
//                     text:
//                         "The selected course plan could not be identified.",
//                 });

//                 return;
//             }

//             const result =
//                 await cayepSwal.fire({
//                     icon: "question",
//                     title:
//                         "Remove Course?",
//                     text:
//                         "This course will be removed from your planned courses.",
//                     showCancelButton: true,
//                     confirmButtonText:
//                         "Remove",
//                     cancelButtonText:
//                         "Keep Course",
//                 });

//             if (
//                 !result.isConfirmed
//             ) {
//                 return;
//             }

//             try {
//                 setCartLoading(true);

//                 await api.delete(
//                     `/api/course-learning/plans/${planId}/remove/`
//                 );

//                 setCart((current) =>
//                     current.filter(
//                         (item) =>
//                             Number(
//                                 getPlanIdFromItem(
//                                     item
//                                 )
//                             ) !==
//                             Number(planId)
//                     )
//                 );

//                 await Promise.all([
//                     fetchCart(),
//                     fetchCourses(),
//                 ]);

//                 await cayepSwal.fire({
//                     icon: "success",
//                     title:
//                         "Course Removed",
//                     text:
//                         "The course has been removed from your planned courses.",
//                     timer: 1800,
//                     showConfirmButton:
//                         false,
//                 });
//             } catch (error) {
//                 console.error(
//                     "Remove course error:",
//                     error
//                 );

//                 await Promise.all([
//                     fetchCart(),
//                     fetchCourses(),
//                 ]);

//                 await cayepSwal.fire({
//                     icon: "error",
//                     title:
//                         "Unable to Remove Course",
//                     text: getErrorMessage(
//                         error,
//                         "The planned course could not be removed."
//                     ),
//                 });
//             } finally {
//                 setCartLoading(false);
//             }
//         };

//     const submitPlan =
//         async () => {
//             if (!cart.length) {
//                 await cayepSwal.fire({
//                     icon: "warning",
//                     title:
//                         "No Courses Selected",
//                     text:
//                         "Please add at least one course before submitting.",
//                 });

//                 return;
//             }

//             if (!paymentMethod) {
//                 await cayepSwal.fire({
//                     icon: "warning",
//                     title:
//                         "Payment Method Required",
//                     text:
//                         "Please select a payment method.",
//                 });

//                 return;
//             }

//             if (
//                 paymentMethod ===
//                     "bank_deposit" &&
//                 !bankName
//             ) {
//                 await cayepSwal.fire({
//                     icon: "warning",
//                     title:
//                         "Bank Required",
//                     text:
//                         "Please select the bank used for your payment.",
//                 });

//                 return;
//             }

//             if (
//                 !transactionReference.trim()
//             ) {
//                 await cayepSwal.fire({
//                     icon: "warning",
//                     title:
//                         "Transaction Reference Required",
//                     text:
//                         "Please enter your payment transaction reference.",
//                 });

//                 return;
//             }

//             if (
//                 !paymentAmount ||
//                 Number(paymentAmount) <=
//                     0
//             ) {
//                 await cayepSwal.fire({
//                     icon: "warning",
//                     title:
//                         "Payment Amount Required",
//                     text:
//                         "Please enter a valid payment amount.",
//                 });

//                 return;
//             }

//             const result =
//                 await cayepSwal.fire({
//                     icon: "question",
//                     title:
//                         "Submit Course Plan?",
//                     html: `
//                         <div style="text-align:left;">
//                             <p>
//                                 You are submitting
//                                 <strong>${cart.length}</strong>
//                                 course${
//                                     cart.length === 1
//                                         ? ""
//                                         : "s"
//                                 }.
//                             </p>
//                             <p>
//                                 Total course value:
//                                 <strong>
//                                     LRD ${formatMoney(
//                                         cartTotal
//                                     )}
//                                 </strong>
//                             </p>
//                         </div>
//                     `,
//                     showCancelButton: true,
//                     confirmButtonText:
//                         "Submit Plan",
//                     cancelButtonText:
//                         "Review",
//                 });

//             if (
//                 !result.isConfirmed
//             ) {
//                 return;
//             }

//             try {
//                 setSubmitting(true);

//                 await api.post(
//                     "/api/course-learning/orders/submit/",
//                     {
//                         payment_method:
//                             paymentMethod,

//                         bank_name:
//                             paymentMethod ===
//                             "bank_deposit"
//                                 ? bankName
//                                 : "",

//                         reference_number:
//                             transactionReference.trim(),

//                         notes: "",
//                     }
//                 );

//                 setCart([]);
//                 setTransactionReference(
//                     ""
//                 );
//                 setPaymentAmount("");
//                 setBankName("");

//                 await Promise.all([
//                     fetchCart(),
//                     fetchCourses(),
//                 ]);

//                 await cayepSwal.fire({
//                     icon: "success",
//                     title:
//                         "Course Plan Submitted",
//                     text:
//                         "Your selected courses have been submitted successfully for processing.",
//                     confirmButtonText:
//                         "Continue",
//                 });

//                 setCartOpen(false);
//             } catch (error) {
//                 console.error(
//                     "Submit plan error:",
//                     error
//                 );

//                 const errorCode =
//                     getErrorCode(error);

//                 await Promise.all([
//                     fetchCart(),
//                     fetchCourses(),
//                 ]);

//                 if (
//                     errorCode ===
//                     "ALREADY_ENROLLED"
//                 ) {
//                     await cayepSwal.fire({
//                         icon: "info",
//                         title:
//                             "Course Already Enrolled",
//                         text:
//                             "One or more selected courses are already enrolled for the current training year. Your planned courses have been refreshed.",
//                     });

//                     return;
//                 }

//                 if (
//                     errorCode ===
//                     "COURSE_FULL"
//                 ) {
//                     await cayepSwal.fire({
//                         icon: "warning",
//                         title:
//                             "Course Capacity Reached",
//                         text:
//                             "One of the selected courses has reached its capacity. Your planned courses have been refreshed.",
//                     });

//                     return;
//                 }

//                 await cayepSwal.fire({
//                     icon: "error",
//                     title:
//                         "Submission Failed",
//                     text: getErrorMessage(
//                         error,
//                         "Your course plan could not be submitted."
//                     ),
//                 });
//             } finally {
//                 setSubmitting(false);
//             }
//         };

//     const cartTotal = useMemo(() => {
//         return cart.reduce(
//             (total, item) => {
//                 const course =
//                     getCartCourse(
//                         item
//                     );

//                 const price =
//                     course?.current_price ??
//                     course?.price ??
//                     item?.unit_price ??
//                     0;

//                 return (
//                     total +
//                     Number(
//                         price || 0
//                     )
//                 );
//             },
//             0
//         );
//     }, [
//         cart,
//         getCartCourse,
//     ]);

//     const categories =
//         useMemo(() => {
//             const values =
//                 courses
//                     .map(
//                         (course) =>
//                             course?.category
//                     )
//                     .filter(Boolean);

//             return [
//                 ...new Set(
//                     values
//                 ),
//             ];
//         }, [courses]);

//     const levels =
//         useMemo(() => {
//             const values =
//                 courses
//                     .map(
//                         (course) =>
//                             course?.level
//                     )
//                     .filter(Boolean);

//             return [
//                 ...new Set(
//                     values
//                 ),
//             ];
//         }, [courses]);

//     const handlePreviousPage =
//         () => {
//             setPage(
//                 (current) =>
//                     Math.max(
//                         1,
//                         current - 1
//                     )
//             );
//         };

//     const handleNextPage =
//         () => {
//             setPage(
//                 (current) =>
//                     Math.min(
//                         totalPages,
//                         current + 1
//                     )
//             );
//         };

//     return (
//         <div className="cayep-plan-page">
//             <style>{`
//                 .cayep-plan-page {
//                     min-height: 100vh;
//                     background: #F4F7F9;
//                     color: #142536;
//                     padding: 24px;
//                 }

//                 .cayep-plan-container {
//                     width: 100%;
//                     max-width: 1440px;
//                     margin: 0 auto;
//                 }

//                 .cayep-plan-header {
//                     display: flex;
//                     justify-content: space-between;
//                     align-items: center;
//                     gap: 20px;
//                     margin-bottom: 22px;
//                 }

//                 .cayep-plan-heading {
//                     display: flex;
//                     align-items: center;
//                     gap: 14px;
//                 }

//                 .cayep-plan-heading-icon {
//                     width: 52px;
//                     height: 52px;
//                     border-radius: 14px;
//                     background: #062A4B;
//                     color: #F6C944;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     font-size: 21px;
//                     flex-shrink: 0;
//                 }

//                 .cayep-plan-heading h1 {
//                     margin: 0;
//                     font-size: 25px;
//                     font-weight: 800;
//                     color: #062A4B;
//                 }

//                 .cayep-plan-heading p {
//                     margin: 4px 0 0;
//                     color: #6B7280;
//                     font-size: 14px;
//                 }

//                 .cayep-cart-toggle {
//                     border: 0;
//                     background: #062A4B;
//                     color: #FFFFFF;
//                     border-radius: 11px;
//                     min-height: 46px;
//                     padding: 0 17px;
//                     display: flex;
//                     align-items: center;
//                     gap: 9px;
//                     cursor: pointer;
//                     font-weight: 700;
//                     box-shadow: 0 5px 15px rgba(6, 42, 75, 0.14);
//                 }

//                 .cayep-cart-toggle:hover {
//                     background: #063A6B;
//                 }

//                 .cayep-cart-count {
//                     min-width: 24px;
//                     height: 24px;
//                     padding: 0 6px;
//                     border-radius: 20px;
//                     background: #F6C944;
//                     color: #062A4B;
//                     display: inline-flex;
//                     align-items: center;
//                     justify-content: center;
//                     font-size: 12px;
//                     font-weight: 800;
//                 }

//                 .cayep-eligibility {
//                     border: 1px solid #E0E7EC;
//                     background: #FFFFFF;
//                     border-radius: 14px;
//                     padding: 15px 17px;
//                     margin-bottom: 18px;
//                     display: flex;
//                     align-items: center;
//                     gap: 12px;
//                     box-shadow: 0 3px 12px rgba(20, 37, 54, 0.04);
//                 }

//                 .cayep-eligibility.success {
//                     border-color: rgba(79, 174, 34, 0.35);
//                     background: #F7FCF4;
//                 }

//                 .cayep-eligibility.warning {
//                     border-color: rgba(192, 117, 13, 0.3);
//                     background: #FFF9F0;
//                 }

//                 .cayep-eligibility-icon {
//                     font-size: 19px;
//                     color: #4FAE22;
//                 }

//                 .cayep-eligibility.warning .cayep-eligibility-icon {
//                     color: #C0750D;
//                 }

//                 .cayep-eligibility-text strong {
//                     display: block;
//                     font-size: 14px;
//                     color: #142536;
//                 }

//                 .cayep-eligibility-text span {
//                     display: block;
//                     margin-top: 2px;
//                     color: #6B7280;
//                     font-size: 13px;
//                 }

//                 .cayep-filters {
//                     background: #FFFFFF;
//                     border: 1px solid #E0E7EC;
//                     border-radius: 14px;
//                     padding: 14px;
//                     margin-bottom: 20px;
//                     display: grid;
//                     grid-template-columns: minmax(220px, 1fr) 190px 170px;
//                     gap: 10px;
//                 }

//                 .cayep-filter-input,
//                 .cayep-filter-select {
//                     width: 100%;
//                     height: 44px;
//                     border: 1px solid #D7E0E6;
//                     background: #FFFFFF;
//                     border-radius: 9px;
//                     padding: 0 13px;
//                     color: #142536;
//                     outline: none;
//                     font-size: 14px;
//                     box-sizing: border-box;
//                 }

//                 .cayep-filter-input:focus,
//                 .cayep-filter-select:focus {
//                     border-color: #0F6DAB;
//                     box-shadow: 0 0 0 3px rgba(15, 109, 171, 0.08);
//                 }

//                 .cayep-course-grid {
//                     display: grid;
//                     grid-template-columns: repeat(3, minmax(0, 1fr));
//                     gap: 18px;
//                 }

//                 .cayep-course-card {
//                     background: #FFFFFF;
//                     border: 1px solid #E0E7EC;
//                     border-radius: 15px;
//                     overflow: hidden;
//                     box-shadow: 0 5px 16px rgba(20, 37, 54, 0.045);
//                     display: flex;
//                     flex-direction: column;
//                     min-width: 0;
//                 }

//                 .cayep-course-image {
//                     height: 190px;
//                     background: #E9EEF2;
//                     position: relative;
//                     overflow: hidden;
//                 }

//                 .cayep-course-image img {
//                     width: 100%;
//                     height: 100%;
//                     display: block;
//                     object-fit: cover;
//                 }

//                 .cayep-course-image-placeholder {
//                     width: 100%;
//                     height: 100%;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     color: #062A4B;
//                     font-size: 40px;
//                 }

//                 .cayep-course-category {
//                     position: absolute;
//                     left: 12px;
//                     top: 12px;
//                     background: rgba(6, 42, 75, 0.92);
//                     color: #FFFFFF;
//                     border-radius: 7px;
//                     padding: 6px 9px;
//                     font-size: 11px;
//                     font-weight: 700;
//                     text-transform: uppercase;
//                     letter-spacing: 0.25px;
//                 }

//                 .cayep-course-body {
//                     padding: 16px;
//                     display: flex;
//                     flex-direction: column;
//                     flex: 1;
//                 }

//                 .cayep-course-title {
//                     margin: 0 0 8px;
//                     font-size: 17px;
//                     line-height: 1.35;
//                     font-weight: 800;
//                     color: #062A4B;
//                 }

//                 .cayep-course-description {
//                     margin: 0 0 13px;
//                     color: #6B7280;
//                     font-size: 13px;
//                     line-height: 1.55;
//                     display: -webkit-box;
//                     -webkit-line-clamp: 3;
//                     -webkit-box-orient: vertical;
//                     overflow: hidden;
//                 }

//                 .cayep-course-meta {
//                     display: flex;
//                     flex-direction: column;
//                     gap: 7px;
//                     margin-bottom: 14px;
//                 }

//                 .cayep-course-meta-row {
//                     display: flex;
//                     align-items: center;
//                     gap: 8px;
//                     color: #6B7280;
//                     font-size: 12px;
//                 }

//                 .cayep-course-meta-row svg {
//                     color: #0F6DAB;
//                     width: 13px;
//                     flex-shrink: 0;
//                 }

//                 .cayep-course-slots {
//                     border: 1px solid #E0E7EC;
//                     background: #F8FAFB;
//                     border-radius: 10px;
//                     padding: 10px;
//                     margin-bottom: 14px;
//                     display: flex;
//                     align-items: center;
//                     justify-content: space-between;
//                     gap: 10px;
//                 }

//                 .cayep-course-slot-info {
//                     display: flex;
//                     align-items: center;
//                     gap: 9px;
//                     min-width: 0;
//                 }

//                 .cayep-course-slot-info > svg {
//                     color: #0F6DAB;
//                     flex-shrink: 0;
//                     font-size: 16px;
//                 }

//                 .cayep-course-slot-text {
//                     min-width: 0;
//                 }

//                 .cayep-course-slot-text strong {
//                     display: block;
//                     color: #142536;
//                     font-size: 12px;
//                     font-weight: 800;
//                     line-height: 1.25;
//                 }

//                 .cayep-course-slot-text span {
//                     display: block;
//                     color: #6B7280;
//                     font-size: 11px;
//                     margin-top: 3px;
//                 }

//                 .cayep-course-drop-time {
//                     margin-top: 6px;
//                     color: #0F6DAB;
//                     font-size: 10px;
//                     font-weight: 700;
//                     display: flex;
//                     align-items: center;
//                     gap: 5px;
//                 }

//                 .cayep-course-drop-time svg {
//                     width: 10px;
//                     color: #C0750D;
//                     flex-shrink: 0;
//                 }

//                 .cayep-slot-badge {
//                     flex-shrink: 0;
//                     border-radius: 20px;
//                     padding: 5px 8px;
//                     background: #EAF6E4;
//                     color: #3C851C;
//                     font-size: 10px;
//                     font-weight: 800;
//                     white-space: nowrap;
//                 }

//                 .cayep-slot-badge.last {
//                     background: #FFF4D6;
//                     color: #A46A00;
//                 }

//                 .cayep-slot-badge.full {
//                     background: #FDECEC;
//                     color: #B42318;
//                 }

//                 .cayep-course-footer {
//                     display: flex;
//                     align-items: center;
//                     justify-content: space-between;
//                     gap: 10px;
//                     margin-top: auto;
//                 }

//                 .cayep-course-price {
//                     color: #062A4B;
//                     font-weight: 900;
//                     font-size: 18px;
//                     white-space: nowrap;
//                 }

//                 .cayep-add-btn {
//                     border: 0;
//                     min-height: 40px;
//                     border-radius: 9px;
//                     padding: 0 13px;
//                     display: inline-flex;
//                     align-items: center;
//                     justify-content: center;
//                     gap: 7px;
//                     background: #062A4B;
//                     color: #FFFFFF;
//                     font-size: 12px;
//                     font-weight: 800;
//                     cursor: pointer;
//                 }

//                 .cayep-add-btn:hover:not(:disabled) {
//                     background: #063A6B;
//                 }

//                 .cayep-add-btn.added {
//                     background: #EAF6E4;
//                     color: #3C851C;
//                     cursor: default;
//                 }

//                 .cayep-add-btn.full {
//                     background: #F1F3F5;
//                     color: #8A929A;
//                     cursor: not-allowed;
//                 }

//                 .cayep-add-btn:disabled {
//                     opacity: 1;
//                 }

//                 .cayep-empty {
//                     background: #FFFFFF;
//                     border: 1px solid #E0E7EC;
//                     border-radius: 15px;
//                     padding: 55px 20px;
//                     text-align: center;
//                     color: #6B7280;
//                     grid-column: 1 / -1;
//                 }

//                 .cayep-empty svg {
//                     color: #C4C7C7;
//                     font-size: 38px;
//                     margin-bottom: 12px;
//                 }

//                 .cayep-empty strong {
//                     display: block;
//                     color: #142536;
//                     font-size: 15px;
//                     margin-bottom: 5px;
//                 }

//                 .cayep-loading {
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     min-height: 250px;
//                     color: #062A4B;
//                     grid-column: 1 / -1;
//                     font-weight: 700;
//                 }

//                 .cayep-pagination {
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     gap: 14px;
//                     margin-top: 22px;
//                 }

//                 .cayep-pagination button {
//                     border: 1px solid #D7E0E6;
//                     background: #FFFFFF;
//                     color: #062A4B;
//                     border-radius: 8px;
//                     min-height: 38px;
//                     padding: 0 13px;
//                     cursor: pointer;
//                     font-weight: 700;
//                 }

//                 .cayep-pagination button:disabled {
//                     color: #A3ABB2;
//                     cursor: not-allowed;
//                     background: #F4F6F7;
//                 }

//                 .cayep-pagination span {
//                     color: #6B7280;
//                     font-size: 13px;
//                     font-weight: 700;
//                 }

//                 .cayep-cart-overlay {
//                     position: fixed;
//                     inset: 0;
//                     background: rgba(6, 15, 22, 0.42);
//                     z-index: 1000;
//                 }

//                 .cayep-cart-panel {
//                     position: fixed;
//                     top: 0;
//                     right: 0;
//                     width: min(440px, 100%);
//                     height: 100vh;
//                     background: #FFFFFF;
//                     z-index: 1001;
//                     box-shadow: -10px 0 35px rgba(6, 42, 75, 0.16);
//                     display: flex;
//                     flex-direction: column;
//                 }

//                 .cayep-cart-header {
//                     padding: 18px;
//                     background: #062A4B;
//                     color: #FFFFFF;
//                     display: flex;
//                     align-items: center;
//                     justify-content: space-between;
//                     gap: 12px;
//                 }

//                 .cayep-cart-header h2 {
//                     margin: 0;
//                     font-size: 18px;
//                 }

//                 .cayep-cart-header p {
//                     margin: 4px 0 0;
//                     color: rgba(255,255,255,0.72);
//                     font-size: 11px;
//                 }

//                 .cayep-cart-header button {
//                     border: 0;
//                     background: transparent;
//                     color: #FFFFFF;
//                     font-size: 23px;
//                     cursor: pointer;
//                     line-height: 1;
//                 }

//                 .cayep-cart-content {
//                     flex: 1;
//                     overflow-y: auto;
//                     padding: 16px;
//                 }

//                 .cayep-cart-item {
//                     border: 1px solid #E0E7EC;
//                     border-radius: 11px;
//                     padding: 10px;
//                     display: flex;
//                     gap: 10px;
//                     margin-bottom: 10px;
//                 }

//                 .cayep-cart-thumb {
//                     width: 62px;
//                     height: 62px;
//                     border-radius: 8px;
//                     background: #EEF2F4;
//                     overflow: hidden;
//                     flex-shrink: 0;
//                 }

//                 .cayep-cart-thumb img {
//                     width: 100%;
//                     height: 100%;
//                     object-fit: cover;
//                 }

//                 .cayep-cart-thumb-placeholder {
//                     width: 100%;
//                     height: 100%;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     color: #062A4B;
//                 }

//                 .cayep-cart-item-info {
//                     min-width: 0;
//                     flex: 1;
//                 }

//                 .cayep-cart-item-title {
//                     color: #062A4B;
//                     font-size: 13px;
//                     font-weight: 800;
//                     margin: 0 0 3px;
//                     line-height: 1.35;
//                 }

//                 .cayep-cart-item-facilitator {
//                     color: #6B7280;
//                     font-size: 11px;
//                     margin-bottom: 6px;
//                 }

//                 .cayep-cart-slot {
//                     display: inline-flex;
//                     align-items: center;
//                     gap: 5px;
//                     color: #3C851C;
//                     background: #F1F9ED;
//                     border-radius: 6px;
//                     padding: 4px 6px;
//                     font-size: 10px;
//                     font-weight: 800;
//                     margin-bottom: 6px;
//                 }

//                 .cayep-cart-slot.last {
//                     color: #A46A00;
//                     background: #FFF7E2;
//                 }

//                 .cayep-cart-slot.full {
//                     color: #B42318;
//                     background: #FDECEC;
//                 }

//                 .cayep-cart-capacity {
//                     color: #6B7280;
//                     font-size: 10px;
//                     margin-left: 4px;
//                 }

//                 .cayep-cart-item-bottom {
//                     display: flex;
//                     align-items: center;
//                     justify-content: space-between;
//                     gap: 8px;
//                 }

//                 .cayep-cart-item-price {
//                     color: #142536;
//                     font-size: 13px;
//                     font-weight: 900;
//                 }

//                 .cayep-cart-remove {
//                     width: 31px;
//                     height: 31px;
//                     border: 0;
//                     border-radius: 7px;
//                     background: #FDECEC;
//                     color: #B42318;
//                     cursor: pointer;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                 }

//                 .cayep-cart-empty {
//                     padding: 45px 15px;
//                     text-align: center;
//                     color: #6B7280;
//                 }

//                 .cayep-cart-empty svg {
//                     font-size: 36px;
//                     color: #C4C7C7;
//                     margin-bottom: 10px;
//                 }

//                 .cayep-payment {
//                     border-top: 1px solid #E0E7EC;
//                     padding: 16px;
//                     background: #FAFBFC;
//                 }

//                 .cayep-payment-title {
//                     margin: 0 0 11px;
//                     color: #062A4B;
//                     font-size: 14px;
//                     font-weight: 900;
//                 }

//                 .cayep-payment-grid {
//                     display: grid;
//                     grid-template-columns: 1fr 1fr;
//                     gap: 9px;
//                 }

//                 .cayep-payment-grid .full {
//                     grid-column: 1 / -1;
//                 }

//                 .cayep-payment label {
//                     display: block;
//                     color: #6B7280;
//                     font-size: 10px;
//                     font-weight: 800;
//                     margin-bottom: 4px;
//                     text-transform: uppercase;
//                 }

//                 .cayep-payment input,
//                 .cayep-payment select {
//                     width: 100%;
//                     height: 39px;
//                     border: 1px solid #D7E0E6;
//                     border-radius: 8px;
//                     padding: 0 9px;
//                     box-sizing: border-box;
//                     background: #FFFFFF;
//                     color: #142536;
//                     outline: none;
//                     font-size: 12px;
//                 }

//                 .cayep-payment input:focus,
//                 .cayep-payment select:focus {
//                     border-color: #0F6DAB;
//                 }

//                 .cayep-cart-total {
//                     margin-top: 13px;
//                     padding-top: 12px;
//                     border-top: 1px solid #E0E7EC;
//                     display: flex;
//                     justify-content: space-between;
//                     align-items: center;
//                 }

//                 .cayep-cart-total span {
//                     color: #6B7280;
//                     font-size: 13px;
//                     font-weight: 700;
//                 }

//                 .cayep-cart-total strong {
//                     color: #062A4B;
//                     font-size: 19px;
//                 }

//                 .cayep-submit-plan {
//                     width: 100%;
//                     min-height: 45px;
//                     margin-top: 12px;
//                     border: 0;
//                     border-radius: 9px;
//                     background: #062A4B;
//                     color: #FFFFFF;
//                     cursor: pointer;
//                     font-size: 13px;
//                     font-weight: 900;
//                 }

//                 .cayep-submit-plan:hover:not(:disabled) {
//                     background: #063A6B;
//                 }

//                 .cayep-submit-plan:disabled {
//                     opacity: 0.55;
//                     cursor: not-allowed;
//                 }

//                 @media (max-width: 1100px) {
//                     .cayep-course-grid {
//                         grid-template-columns: repeat(2, minmax(0, 1fr));
//                     }
//                 }

//                 @media (max-width: 760px) {
//                     .cayep-plan-page {
//                         padding: 15px;
//                     }

//                     .cayep-plan-header {
//                         align-items: flex-start;
//                     }

//                     .cayep-plan-heading h1 {
//                         font-size: 21px;
//                     }

//                     .cayep-filters {
//                         grid-template-columns: 1fr;
//                     }

//                     .cayep-course-grid {
//                         grid-template-columns: 1fr;
//                     }
//                 }

//                 @media (max-width: 520px) {
//                     .cayep-plan-header {
//                         flex-direction: column;
//                     }

//                     .cayep-cart-toggle {
//                         width: 100%;
//                         justify-content: center;
//                     }

//                     .cayep-course-image {
//                         height: 175px;
//                     }

//                     .cayep-course-slots {
//                         align-items: flex-start;
//                     }

//                     .cayep-course-footer {
//                         align-items: flex-end;
//                     }

//                     .cayep-payment-grid {
//                         grid-template-columns: 1fr;
//                     }

//                     .cayep-payment-grid .full {
//                         grid-column: auto;
//                     }
//                 }
//             `}</style>

//             <div className="cayep-plan-container">
//                 <div className="cayep-plan-header">
//                     <div className="cayep-plan-heading">
//                         <div className="cayep-plan-heading-icon">
//                             <FaGraduationCap />
//                         </div>

//                         <div>
//                             <h1>
//                                 Plan Your Courses
//                             </h1>

//                             <p>
//                                 Select one or more courses
//                                 you want to take for your
//                                 current training year.
//                             </p>
//                         </div>
//                     </div>

//                     <button
//                         type="button"
//                         className="cayep-cart-toggle"
//                         onClick={() =>
//                             setCartOpen(true)
//                         }
//                     >
//                         <FaShoppingCart />

//                         Planned Courses

//                         <span className="cayep-cart-count">
//                             {cart.length}
//                         </span>
//                     </button>
//                 </div>

//                 {!eligibilityLoading && (
//                     <div
//                         className={`cayep-eligibility ${
//                             isEligible
//                                 ? "success"
//                                 : "warning"
//                         }`}
//                     >
//                         {isEligible ? (
//                             <FaCheckCircle className="cayep-eligibility-icon" />
//                         ) : (
//                             <FaExclamationTriangle className="cayep-eligibility-icon" />
//                         )}

//                         <div className="cayep-eligibility-text">
//                             <strong>
//                                 {isEligible
//                                     ? "You are eligible to plan courses"
//                                     : "Course planning is currently unavailable"}
//                             </strong>

//                             <span>
//                                 {eligibility?.message ||
//                                     (isEligible
//                                         ? "You can select one or more courses from the catalog below."
//                                         : "Please complete the required eligibility conditions before planning courses.")}
//                             </span>
//                         </div>
//                     </div>
//                 )}

//                 <div className="cayep-filters">
//                     <input
//                         type="search"
//                         className="cayep-filter-input"
//                         placeholder="Search courses..."
//                         value={search}
//                         onChange={(event) =>
//                             setSearch(
//                                 event.target.value
//                             )
//                         }
//                     />

//                     <select
//                         className="cayep-filter-select"
//                         value={category}
//                         onChange={(event) =>
//                             setCategory(
//                                 event.target.value
//                             )
//                         }
//                     >
//                         <option value="">
//                             All Categories
//                         </option>

//                         {categories.map(
//                             (item) => (
//                                 <option
//                                     key={item}
//                                     value={item}
//                                 >
//                                     {item}
//                                 </option>
//                             )
//                         )}
//                     </select>

//                     <select
//                         className="cayep-filter-select"
//                         value={level}
//                         onChange={(event) =>
//                             setLevel(
//                                 event.target.value
//                             )
//                         }
//                     >
//                         <option value="">
//                             All Levels
//                         </option>

//                         {levels.map(
//                             (item) => (
//                                 <option
//                                     key={item}
//                                     value={item}
//                                 >
//                                     {item}
//                                 </option>
//                             )
//                         )}
//                     </select>
//                 </div>

//                 <div className="cayep-course-grid">
//                     {loading ? (
//                         <div className="cayep-loading">
//                             Loading courses...
//                         </div>
//                     ) : courses.length === 0 ? (
//                         <div className="cayep-empty">
//                             <FaBookOpen />

//                             <strong>
//                                 No courses found
//                             </strong>

//                             <span>
//                                 Try changing your
//                                 search or filter
//                                 options.
//                             </span>
//                         </div>
//                     ) : (
//                         courses.map(
//                             (course) => {
//                                 const slotState =
//                                     getSlotState(
//                                         course
//                                     );

//                                 const inCart =
//                                     isInCart(
//                                         course.id
//                                     );

//                                 return (
//                                     <div
//                                         className="cayep-course-card"
//                                         key={
//                                             course.id
//                                         }
//                                     >
//                                         <div className="cayep-course-image">
//                                             {course.thumbnail ||
//                                             course.image ? (
//                                                 <img
//                                                     src={
//                                                         course.thumbnail ||
//                                                         course.image
//                                                     }
//                                                     alt={
//                                                         course.course_title ||
//                                                         course.name
//                                                     }
//                                                 />
//                                             ) : (
//                                                 <div className="cayep-course-image-placeholder">
//                                                     <FaBookOpen />
//                                                 </div>
//                                             )}

//                                             {course.category && (
//                                                 <span className="cayep-course-category">
//                                                     {
//                                                         course.category
//                                                     }
//                                                 </span>
//                                             )}
//                                         </div>

//                                         <div className="cayep-course-body">
//                                             <h2 className="cayep-course-title">
//                                                 {course.course_title ||
//                                                     course.name}
//                                             </h2>

//                                             <p className="cayep-course-description">
//                                                 {course.description ||
//                                                     "Professional training course designed to develop practical knowledge and leadership skills."}
//                                             </p>

//                                             <div className="cayep-course-meta">
//                                                 {course.facilitator && (
//                                                     <div className="cayep-course-meta-row">
//                                                         <FaUserTie />

//                                                         <span>
//                                                             {
//                                                                 course.facilitator
//                                                             }
//                                                         </span>
//                                                     </div>
//                                                 )}

//                                                 {course.level && (
//                                                     <div className="cayep-course-meta-row">
//                                                         <FaGraduationCap />

//                                                         <span>
//                                                             {
//                                                                 course.level
//                                                             }
//                                                         </span>
//                                                     </div>
//                                                 )}

//                                                 {course.duration && (
//                                                     <div className="cayep-course-meta-row">
//                                                         <FaClock />

//                                                         <span>
//                                                             {
//                                                                 course.duration
//                                                             }
//                                                         </span>
//                                                     </div>
//                                                 )}

//                                                 {course.video_url && (
//                                                     <div className="cayep-course-meta-row">
//                                                         <FaVideo />

//                                                         <span>
//                                                             Video learning available
//                                                         </span>
//                                                     </div>
//                                                 )}
//                                             </div>

//                                             <div className="cayep-course-slots">
//                                                 <div className="cayep-course-slot-info">
//                                                     {slotState.full ? (
//                                                         <FaExclamationTriangle />
//                                                     ) : (
//                                                         <FaUsers />
//                                                     )}

//                                                     <div className="cayep-course-slot-text">
//                                                         <strong>
//                                                             {slotState.full
//                                                                 ? "Course Capacity"
//                                                                 : slotState.unlimited
//                                                                 ? "Course Capacity"
//                                                                 : `${slotState.remaining} ${
//                                                                       slotState.remaining ===
//                                                                       1
//                                                                           ? "slot"
//                                                                           : "slots"
//                                                                   } available`}
//                                                         </strong>

//                                                         <span>
//                                                             {slotState.unlimited
//                                                                 ? "Open enrollment"
//                                                                 : `${slotState.taken} of ${slotState.total} ${
//                                                                       slotState.total ===
//                                                                       1
//                                                                           ? "seat"
//                                                                           : "seats"
//                                                                   } occupied`}
//                                                         </span>

//                                                         {course.latest_drop_ago && (
//                                                             <div className="cayep-course-drop-time">
//                                                                 <FaClock />

//                                                                 <span>
//                                                                     Seat released{" "}
//                                                                     {
//                                                                         course.latest_drop_ago
//                                                                     }
//                                                                 </span>
//                                                             </div>
//                                                         )}
//                                                     </div>
//                                                 </div>

//                                                 <span
//                                                     className={`cayep-slot-badge ${
//                                                         slotState.full
//                                                             ? "full"
//                                                             : slotState.lastSlot
//                                                             ? "last"
//                                                             : ""
//                                                     }`}
//                                                 >
//                                                     {getSlotLabel(
//                                                         course
//                                                     )}
//                                                 </span>
//                                             </div>

//                                             <div className="cayep-course-footer">
//                                                 <div className="cayep-course-price">
//                                                     {course.current_price ||
//                                                     course.price
//                                                         ? `LRD ${formatMoney(
//                                                               course.current_price ??
//                                                                   course.price
//                                                           )}`
//                                                         : "Free"}
//                                                 </div>

//                                                 <button
//                                                     type="button"
//                                                     className={`cayep-add-btn ${
//                                                         inCart
//                                                             ? "added"
//                                                             : slotState.full
//                                                             ? "full"
//                                                             : ""
//                                                     }`}
//                                                     disabled={
//                                                         inCart ||
//                                                         slotState.full ||
//                                                         !isEligible ||
//                                                         cartLoading ||
//                                                         refreshingSlots
//                                                     }
//                                                     onClick={() =>
//                                                         addToCart(
//                                                             course
//                                                         )
//                                                     }
//                                                 >
//                                                     {inCart ? (
//                                                         <>
//                                                             <FaCheckCircle />
//                                                             Planned
//                                                         </>
//                                                     ) : slotState.full ? (
//                                                         <>
//                                                             <FaExclamationTriangle />
//                                                             Class Full
//                                                         </>
//                                                     ) : (
//                                                         <>
//                                                             <FaPlus />
//                                                             Add Course
//                                                         </>
//                                                     )}
//                                                 </button>
//                                             </div>
//                                         </div>
//                                     </div>
//                                 );
//                             }
//                         )
//                     )}
//                 </div>

//                 {!loading &&
//                     courses.length > 0 && (
//                         <div className="cayep-pagination">
//                             <button
//                                 type="button"
//                                 disabled={
//                                     page <= 1
//                                 }
//                                 onClick={
//                                     handlePreviousPage
//                                 }
//                             >
//                                 Previous
//                             </button>

//                             <span>
//                                 Page {page} of{" "}
//                                 {totalPages}
//                             </span>

//                             <button
//                                 type="button"
//                                 disabled={
//                                     page >=
//                                     totalPages
//                                 }
//                                 onClick={
//                                     handleNextPage
//                                 }
//                             >
//                                 Next
//                             </button>
//                         </div>
//                     )}
//             </div>

//             {cartOpen && (
//                 <>
//                     <div
//                         className="cayep-cart-overlay"
//                         onClick={() =>
//                             setCartOpen(false)
//                         }
//                     />

//                     <aside className="cayep-cart-panel">
//                         <div className="cayep-cart-header">
//                             <div>
//                                 <h2>
//                                     Planned Courses
//                                 </h2>

//                                 <p>
//                                     {cart.length} course
//                                     {cart.length === 1
//                                         ? ""
//                                         : "s"} selected
//                                 </p>
//                             </div>

//                             <button
//                                 type="button"
//                                 onClick={() =>
//                                     setCartOpen(
//                                         false
//                                     )
//                                 }
//                                 aria-label="Close"
//                             >
//                                 ×
//                             </button>
//                         </div>

//                         <div className="cayep-cart-content">
//                             {cartLoading ? (
//                                 <div className="cayep-cart-empty">
//                                     Loading planned
//                                     courses...
//                                 </div>
//                             ) : cart.length === 0 ? (
//                                 <div className="cayep-cart-empty">
//                                     <FaShoppingCart />

//                                     <div>
//                                         You have not
//                                         planned any
//                                         courses yet.
//                                     </div>
//                                 </div>
//                             ) : (
//                                 cart.map(
//                                     (item) => {
//                                         const course =
//                                             getCartCourse(
//                                                 item
//                                             );

//                                         const planId =
//                                             getPlanId(
//                                                 item
//                                             );

//                                         const slotState =
//                                             getSlotState(
//                                                 course
//                                             );

//                                         return (
//                                             <div
//                                                 className="cayep-cart-item"
//                                                 key={
//                                                     planId ||
//                                                     course?.id
//                                                 }
//                                             >
//                                                 <div className="cayep-cart-thumb">
//                                                     {course?.thumbnail ||
//                                                     course?.image ? (
//                                                         <img
//                                                             src={
//                                                                 course.thumbnail ||
//                                                                 course.image
//                                                             }
//                                                             alt={
//                                                                 course.course_title ||
//                                                                 course.name
//                                                             }
//                                                         />
//                                                     ) : (
//                                                         <div className="cayep-cart-thumb-placeholder">
//                                                             <FaBookOpen />
//                                                         </div>
//                                                     )}
//                                                 </div>

//                                                 <div className="cayep-cart-item-info">
//                                                     <h3 className="cayep-cart-item-title">
//                                                         {course?.course_title ||
//                                                             course?.name}
//                                                     </h3>

//                                                     {course?.facilitator && (
//                                                         <div className="cayep-cart-item-facilitator">
//                                                             {
//                                                                 course.facilitator
//                                                             }
//                                                         </div>
//                                                     )}

//                                                     <div
//                                                         className={`cayep-cart-slot ${
//                                                             slotState.full
//                                                                 ? "full"
//                                                                 : slotState.lastSlot
//                                                                 ? "last"
//                                                                 : ""
//                                                         }`}
//                                                     >
//                                                         {slotState.full ? (
//                                                             <FaExclamationTriangle />
//                                                         ) : (
//                                                             <FaUsers />
//                                                         )}

//                                                         <span>
//                                                             {slotState.full
//                                                                 ? "Class is currently full"
//                                                                 : slotState.unlimited
//                                                                 ? "Open enrollment"
//                                                                 : `${slotState.remaining} ${
//                                                                       slotState.remaining ===
//                                                                       1
//                                                                           ? "slot"
//                                                                           : "slots"
//                                                                   } available`}
//                                                         </span>

//                                                         {!slotState.unlimited && (
//                                                             <span className="cayep-cart-capacity">
//                                                                 {
//                                                                     slotState.taken
//                                                                 }
//                                                                 /
//                                                                 {
//                                                                     slotState.total
//                                                                 }
//                                                             </span>
//                                                         )}
//                                                     </div>

//                                                     {course?.latest_drop_ago && (
//                                                         <div className="cayep-course-drop-time">
//                                                             <FaClock />

//                                                             <span>
//                                                                 Seat released{" "}
//                                                                 {
//                                                                     course.latest_drop_ago
//                                                                 }
//                                                             </span>
//                                                         </div>
//                                                     )}

//                                                     <div className="cayep-cart-item-bottom">
//                                                         <span className="cayep-cart-item-price">
//                                                             {course?.current_price ||
//                                                             course?.price
//                                                                 ? `LRD ${formatMoney(
//                                                                       course.current_price ??
//                                                                           course.price
//                                                                   )}`
//                                                                 : "Free"}
//                                                         </span>

//                                                         <button
//                                                             type="button"
//                                                             className="cayep-cart-remove"
//                                                             disabled={
//                                                                 cartLoading ||
//                                                                 !planId
//                                                             }
//                                                             onClick={() =>
//                                                                 removeFromCart(
//                                                                     planId
//                                                                 )
//                                                             }
//                                                             title="Remove course"
//                                                         >
//                                                             <FaTrash />
//                                                         </button>
//                                                     </div>
//                                                 </div>
//                                             </div>
//                                         );
//                                     }
//                                 )
//                             )}
//                         </div>

//                         {cart.length > 0 && (
//                             <div className="cayep-payment">
//                                 <h3 className="cayep-payment-title">
//                                     Payment Information
//                                 </h3>

//                                 <div className="cayep-payment-grid">
//                                     <div>
//                                         <label>
//                                             Payment
//                                             Method
//                                         </label>

//                                         <select
//                                             value={
//                                                 paymentMethod
//                                             }
//                                             onChange={(
//                                                 event
//                                             ) =>
//                                                 setPaymentMethod(
//                                                     event
//                                                         .target
//                                                         .value
//                                                 )
//                                             }
//                                         >
//                                             <option value="bank_deposit">
//                                                 Bank
//                                                 Deposit
//                                             </option>

//                                             <option value="cash">
//                                                 Cash
//                                             </option>

//                                             <option value="mobile_money">
//                                                 Mobile
//                                                 Money
//                                             </option>
//                                         </select>
//                                     </div>

//                                     {paymentMethod ===
//                                         "bank_deposit" && (
//                                         <div>
//                                             <label>
//                                                 Bank
//                                             </label>

//                                             <select
//                                                 value={
//                                                     bankName
//                                                 }
//                                                 onChange={(
//                                                     event
//                                                 ) =>
//                                                     setBankName(
//                                                         event
//                                                             .target
//                                                             .value
//                                                     )
//                                                 }
//                                             >
//                                                 <option value="">
//                                                     Select
//                                                     Bank
//                                                 </option>

//                                                 {BANKS.map(
//                                                     (
//                                                         bank
//                                                     ) => (
//                                                         <option
//                                                             key={
//                                                                 bank
//                                                             }
//                                                             value={
//                                                                 bank
//                                                             }
//                                                         >
//                                                             {
//                                                                 bank
//                                                             }
//                                                         </option>
//                                                     )
//                                                 )}
//                                             </select>
//                                         </div>
//                                     )}

//                                     <div>
//                                         <label>
//                                             Payment
//                                             Amount
//                                         </label>

//                                         <input
//                                             type="number"
//                                             min="0"
//                                             step="0.01"
//                                             value={
//                                                 paymentAmount
//                                             }
//                                             onChange={(
//                                                 event
//                                             ) =>
//                                                 setPaymentAmount(
//                                                     event
//                                                         .target
//                                                         .value
//                                                 )
//                                             }
//                                             placeholder="0.00"
//                                         />
//                                     </div>

//                                     <div>
//                                         <label>
//                                             Transaction
//                                             Reference
//                                         </label>

//                                         <input
//                                             type="text"
//                                             value={
//                                                 transactionReference
//                                             }
//                                             onChange={(
//                                                 event
//                                             ) =>
//                                                 setTransactionReference(
//                                                     event
//                                                         .target
//                                                         .value
//                                                 )
//                                             }
//                                             placeholder="Transaction reference"
//                                         />
//                                     </div>
//                                 </div>

//                                 <div className="cayep-cart-total">
//                                     <span>
//                                         {cart.length} Course
//                                         {cart.length === 1
//                                             ? ""
//                                             : "s"} Total
//                                     </span>

//                                     <strong>
//                                         LRD{" "}
//                                         {formatMoney(
//                                             cartTotal
//                                         )}
//                                     </strong>
//                                 </div>

//                                 <button
//                                     type="button"
//                                     className="cayep-submit-plan"
//                                     disabled={
//                                         submitting ||
//                                         cartLoading ||
//                                         refreshingSlots
//                                     }
//                                     onClick={
//                                         submitPlan
//                                     }
//                                 >
//                                     {submitting
//                                         ? "Submitting..."
//                                         : `Submit ${cart.length} Course${
//                                               cart.length ===
//                                               1
//                                                   ? ""
//                                                   : "s"
//                                           }`}
//                                 </button>
//                             </div>
//                         )}
//                     </aside>
//                 </>
//             )}
//         </div>
//     );
// };

// export default PlanCoursesPage;








// import React, {
//     useCallback,
//     useEffect,
//     useMemo,
//     useState,
// } from "react";
// import Swal from "sweetalert2";

// import {
//     FaBookOpen,
//     FaCheckCircle,
//     FaClock,
//     FaExclamationTriangle,
//     FaGraduationCap,
//     FaPlus,
//     FaShoppingCart,
//     FaTrash,
//     FaUsers,
//     FaUserTie,
//     FaVideo,
// } from "react-icons/fa";

// import api from "../../api/api";
// import { useAcademicYear } from "../../AcademicYearContext";

// const formatMoney = (value) => {
//     const amount = Number(value || 0);

//     return new Intl.NumberFormat("en-US", {
//         minimumFractionDigits: 2,
//         maximumFractionDigits: 2,
//     }).format(amount);
// };

// const getErrorMessage = (
//     error,
//     fallback = "Something went wrong."
// ) => {
//     const data = error?.response?.data;

//     if (!data) {
//         return error?.message || fallback;
//     }

//     if (typeof data === "string") {
//         return data;
//     }

//     if (data.detail) {
//         return data.detail;
//     }

//     if (data.message) {
//         return data.message;
//     }

//     if (data.error) {
//         return data.error;
//     }

//     if (data.non_field_errors?.length) {
//         return data.non_field_errors[0];
//     }

//     const firstKey = Object.keys(data)[0];

//     if (firstKey) {
//         const value = data[firstKey];

//         if (Array.isArray(value)) {
//             return value[0];
//         }

//         if (typeof value === "string") {
//             return value;
//         }

//         if (
//             value &&
//             typeof value === "object"
//         ) {
//             if (value.detail) {
//                 return value.detail;
//             }

//             if (value.message) {
//                 return value.message;
//             }
//         }
//     }

//     return fallback;
// };

// const getErrorCode = (error) => {
//     const data = error?.response?.data;

//     if (!data || typeof data !== "object") {
//         return null;
//     }

//     return (
//         data.code ||
//         data.error_code ||
//         data.errorCode ||
//         null
//     );
// };

// const cayepSwal = Swal.mixin({
//     confirmButtonColor: "#062A4B",
//     cancelButtonColor: "#6B7280",
//     background: "#FFFFFF",
//     color: "#142536",
// });

// const BANKS = [
//     "LBDI",
//     "Ecobank Liberia",
//     "International Bank Liberia",
//     "Bloom Bank Africa",
//     "Sapelle International Bank",
//     "UBA Liberia",
//     "Access Bank Liberia",
//     "GTBank Liberia",
//     "Afriland First Bank Liberia",
// ];

// const getSlotState = (course) => {
//     const rawTotal = Number(
//         course?.slots ?? 0
//     );

//     const rawTaken = Number(
//         course?.slots_taken ?? 0
//     );

//     const total = Number.isFinite(rawTotal)
//         ? Math.max(0, rawTotal)
//         : 0;

//     const taken = Number.isFinite(rawTaken)
//         ? Math.max(0, rawTaken)
//         : 0;

//     const unlimited =
//         total === 0 &&
//         course?.is_full !== true;

//     const remaining = unlimited
//         ? null
//         : Math.max(
//               0,
//               Number(
//                   course?.slots_remaining ??
//                       Math.max(
//                           total - taken,
//                           0
//                       )
//               )
//           );

//     const full =
//         !unlimited &&
//         (
//             course?.is_full === true ||
//             remaining <= 0
//         );

//     const lastSlot =
//         !unlimited &&
//         !full &&
//         remaining === 1;

//     return {
//         total,
//         taken,
//         remaining,
//         full,
//         lastSlot,
//         unlimited,
//     };
// };

// const getSlotLabel = (course) => {
//     const slotState =
//         getSlotState(course);

//     if (slotState.full) {
//         return "Class Full";
//     }

//     if (slotState.unlimited) {
//         return "Open";
//     }

//     if (slotState.lastSlot) {
//         return "Last Slot";
//     }

//     return `${slotState.remaining} Available`;
// };

// const getCourseFromPlan = (item) => {
//     if (!item) {
//         return null;
//     }

//     return (
//         item.course ||
//         item.subject ||
//         null
//     );
// };

// const getPlanIdFromItem = (item) => {
//     if (!item) {
//         return null;
//     }

//     return (
//         item.id ||
//         item.plan_id ||
//         item.course_plan_id ||
//         null
//     );
// };

// const getCourseIdFromPlan = (item) => {
//     const course =
//         getCourseFromPlan(item);

//     if (course?.id != null) {
//         return Number(course.id);
//     }

//     if (item?.course_id != null) {
//         return Number(item.course_id);
//     }

//     if (item?.subject_id != null) {
//         return Number(item.subject_id);
//     }

//     return null;
// };

// const normalizePlanItems = (items) => {
//     if (!Array.isArray(items)) {
//         return [];
//     }

//     const seenPlanIds = new Set();
//     const seenCourseIds = new Set();

//     return items.filter((item) => {
//         const planId =
//             getPlanIdFromItem(item);

//         const courseId =
//             getCourseIdFromPlan(item);

//         if (
//             planId != null &&
//             seenPlanIds.has(planId)
//         ) {
//             return false;
//         }

//         if (
//             courseId != null &&
//             seenCourseIds.has(courseId)
//         ) {
//             return false;
//         }

//         if (planId != null) {
//             seenPlanIds.add(planId);
//         }

//         if (courseId != null) {
//             seenCourseIds.add(courseId);
//         }

//         return true;
//     });
// };

// const PlanCoursesPage = () => {
//     const { selectedYear } =
//         useAcademicYear();

//     const [courses, setCourses] =
//         useState([]);

//     const [cart, setCart] =
//         useState([]);

//     const [loading, setLoading] =
//         useState(true);

//     const [cartLoading, setCartLoading] =
//         useState(false);

//     const [cartOpen, setCartOpen] =
//         useState(false);

//     const [search, setSearch] =
//         useState("");

//     const [category, setCategory] =
//         useState("");

//     const [level, setLevel] =
//         useState("");

//     const [page, setPage] =
//         useState(1);

//     const [pageSize] =
//         useState(12);

//     const [totalPages, setTotalPages] =
//         useState(1);

//     const [
//         paymentMethod,
//         setPaymentMethod,
//     ] = useState("bank_deposit");

//     const [bankName, setBankName] =
//         useState("");

//     const [
//         transactionReference,
//         setTransactionReference,
//     ] = useState("");

//     const [paymentAmount, setPaymentAmount] =
//         useState("");

//     const [submitting, setSubmitting] =
//         useState(false);

//     const [eligibility, setEligibility] =
//         useState(null);

//     const [
//         eligibilityLoading,
//         setEligibilityLoading,
//     ] = useState(true);

//     const [
//         refreshingSlots,
//         setRefreshingSlots,
//     ] = useState(false);

//     const fetchEligibility =
//         useCallback(async () => {
//             setEligibilityLoading(true);

//             try {
//                 const response =
//                     await api.get(
//                         "/api/course-learning/eligibility/status/"
//                     );

//                 setEligibility(
//                     response.data
//                 );
//             } catch (error) {
//                 console.error(
//                     "Eligibility error:",
//                     error
//                 );

//                 setEligibility({
//                     eligible: false,
//                     message:
//                         getErrorMessage(
//                             error,
//                             "Unable to verify course eligibility."
//                         ),
//                 });
//             } finally {
//                 setEligibilityLoading(
//                     false
//                 );
//             }
//         }, []);

//     const fetchCart =
//         useCallback(async () => {
//             setCartLoading(true);

//             try {
//                 const response =
//                     await api.get(
//                         "/api/course-learning/plans/"
//                     );

//                 const data =
//                     response.data;

//                 const items =
//                     Array.isArray(data)
//                         ? data
//                         : data?.items ||
//                           data?.results ||
//                           [];

//                 setCart(
//                     normalizePlanItems(
//                         items
//                     )
//                 );
//             } catch (error) {
//                 console.error(
//                     "Cart error:",
//                     error
//                 );

//                 setCart([]);
//             } finally {
//                 setCartLoading(false);
//             }
//         }, []);

//     const fetchCourses =
//         useCallback(async () => {
//             setLoading(true);

//             try {
//                 const params = {
//                     page,
//                     page_size: pageSize,
//                 };

//                 if (search.trim()) {
//                     params.search =
//                         search.trim();
//                 }

//                 if (category) {
//                     params.category =
//                         category;
//                 }

//                 if (level) {
//                     params.level = level;
//                 }

//                 const response =
//                     await api.get(
//                         "/api/course-learning/courses/",
//                         {
//                             params,
//                         }
//                     );

//                 const data =
//                     response.data;

//                 if (Array.isArray(data)) {
//                     setCourses(data);
//                     setTotalPages(1);
//                     return;
//                 }

//                 const results =
//                     data?.results || [];

//                 setCourses(results);

//                 const count =
//                     Number(
//                         data?.count || 0
//                     );

//                 setTotalPages(
//                     Math.max(
//                         1,
//                         Math.ceil(
//                             count /
//                                 pageSize
//                         )
//                     )
//                 );
//             } catch (error) {
//                 console.error(
//                     "Courses error:",
//                     error
//                 );

//                 setCourses([]);
//                 setTotalPages(1);

//                 await cayepSwal.fire({
//                     icon: "error",
//                     title:
//                         "Unable to Load Courses",
//                     text: getErrorMessage(
//                         error,
//                         "The course catalog could not be loaded."
//                     ),
//                 });
//             } finally {
//                 setLoading(false);
//             }
//         }, [
//             category,
//             level,
//             page,
//             pageSize,
//             search,
//         ]);

//     const refreshCourseData =
//         useCallback(async () => {
//             setRefreshingSlots(true);

//             try {
//                 await Promise.all([
//                     fetchCourses(),
//                     fetchCart(),
//                 ]);
//             } finally {
//                 setRefreshingSlots(false);
//             }
//         }, [
//             fetchCourses,
//             fetchCart,
//         ]);

//     useEffect(() => {
//         fetchEligibility();
//     }, [fetchEligibility]);

//     useEffect(() => {
//         fetchCart();
//     }, [
//         fetchCart,
//         selectedYear,
//     ]);

//     useEffect(() => {
//         fetchCourses();
//     }, [
//         fetchCourses,
//         selectedYear,
//     ]);

//     useEffect(() => {
//         setPage(1);
//     }, [
//         search,
//         category,
//         level,
//     ]);

//     const isEligible = useMemo(() => {
//         if (!eligibility) {
//             return false;
//         }

//         return Boolean(
//             eligibility.eligible ??
//                 eligibility.is_eligible ??
//                 eligibility.can_plan_courses ??
//                 eligibility.can_plan
//         );
//     }, [eligibility]);

//     const isInCart =
//         useCallback(
//             (courseId) => {
//                 const normalizedCourseId =
//                     Number(courseId);

//                 if (
//                     !Number.isFinite(
//                         normalizedCourseId
//                     )
//                 ) {
//                     return false;
//                 }

//                 return cart.some(
//                     (item) =>
//                         getCourseIdFromPlan(
//                             item
//                         ) ===
//                         normalizedCourseId
//                 );
//             },
//             [cart]
//         );

//     const getCartCourse =
//         useCallback((item) => {
//             return (
//                 getCourseFromPlan(
//                     item
//                 ) || item
//             );
//         }, []);

//     const getPlanId =
//         useCallback((item) => {
//             return getPlanIdFromItem(
//                 item
//             );
//         }, []);

//     const addToCart = async (
//         course
//     ) => {
//         if (!isEligible) {
//             await cayepSwal.fire({
//                 icon: "warning",
//                 title: "Not Eligible",
//                 text:
//                     eligibility?.message ||
//                     "You are not currently eligible to plan courses.",
//             });

//             return;
//         }

//         const slotState =
//             getSlotState(course);

//         if (slotState.full) {
//             await cayepSwal.fire({
//                 icon: "warning",
//                 title: "Class Full",
//                 text:
//                     course?.slot_status ||
//                     "This course has reached its maximum capacity.",
//             });

//             await fetchCourses();

//             return;
//         }

//         if (isInCart(course.id)) {
//             await cayepSwal.fire({
//                 icon: "info",
//                 title: "Already Planned",
//                 text:
//                     "This course is already in your planned courses.",
//             });

//             return;
//         }

//         try {
//             setCartLoading(true);

//             await api.post(
//                 "/api/course-learning/plans/add/",
//                 {
//                     course_id:
//                         course.id,
//                 }
//             );

//             await Promise.all([
//                 fetchCart(),
//                 fetchCourses(),
//             ]);

//             await cayepSwal.fire({
//                 icon: "success",
//                 title: "Course Added",
//                 text: `${
//                     course.course_title ||
//                     course.name
//                 } has been added to your planned courses.`,
//                 timer: 1800,
//                 showConfirmButton: false,
//             });
//         } catch (error) {
//             console.error(
//                 "Add course error:",
//                 error
//             );

//             const errorCode =
//                 getErrorCode(error);

//             const errorText =
//                 String(
//                     getErrorMessage(
//                         error,
//                         ""
//                     )
//                 ).toLowerCase();

//             await Promise.all([
//                 fetchCourses(),
//                 fetchCart(),
//             ]);

//             if (
//                 errorCode ===
//                     "COURSE_FULL" ||
//                 errorCode ===
//                     "NO_SLOTS" ||
//                 errorText.includes(
//                     "capacity"
//                 ) ||
//                 errorText.includes(
//                     "class full"
//                 )
//             ) {
//                 await cayepSwal.fire({
//                     icon: "warning",
//                     title:
//                         "Class Full",
//                     text: getErrorMessage(
//                         error,
//                         "This course has just reached its maximum capacity."
//                     ),
//                 });

//                 return;
//             }

//             if (
//                 errorCode ===
//                 "ALREADY_ENROLLED"
//             ) {
//                 await cayepSwal.fire({
//                     icon: "info",
//                     title:
//                         "Already Enrolled",
//                     text:
//                         "You are already enrolled in this course for the current training year.",
//                 });

//                 return;
//             }

//             if (
//                 errorCode ===
//                 "COURSE_COMPLETED"
//             ) {
//                 await cayepSwal.fire({
//                     icon: "info",
//                     title:
//                         "Course Completed",
//                     text:
//                         "You have already completed this course and cannot plan it again.",
//                 });

//                 return;
//             }

//             if (
//                 errorCode ===
//                 "ALREADY_PLANNED"
//             ) {
//                 await fetchCart();

//                 await cayepSwal.fire({
//                     icon: "info",
//                     title:
//                         "Already Planned",
//                     text:
//                         "This course is already in your planned courses.",
//                 });

//                 return;
//             }

//             await cayepSwal.fire({
//                 icon: "error",
//                 title:
//                     "Unable to Add Course",
//                 text: getErrorMessage(
//                     error,
//                     "The course could not be added to your planned courses."
//                 ),
//             });
//         } finally {
//             setCartLoading(false);
//         }
//     };

//     const removeFromCart =
//         async (planId) => {
//             if (!planId) {
//                 await cayepSwal.fire({
//                     icon: "error",
//                     title:
//                         "Invalid Course Plan",
//                     text:
//                         "The selected course plan could not be identified.",
//                 });

//                 return;
//             }

//             const result =
//                 await cayepSwal.fire({
//                     icon: "question",
//                     title:
//                         "Remove Course?",
//                     text:
//                         "This course will be removed from your planned courses.",
//                     showCancelButton: true,
//                     confirmButtonText:
//                         "Remove",
//                     cancelButtonText:
//                         "Keep Course",
//                 });

//             if (
//                 !result.isConfirmed
//             ) {
//                 return;
//             }

//             try {
//                 setCartLoading(true);

//                 await api.delete(
//                     `/api/course-learning/plans/${planId}/remove/`
//                 );

//                 setCart((current) =>
//                     current.filter(
//                         (item) =>
//                             Number(
//                                 getPlanIdFromItem(
//                                     item
//                                 )
//                             ) !==
//                             Number(planId)
//                     )
//                 );

//                 await Promise.all([
//                     fetchCart(),
//                     fetchCourses(),
//                 ]);

//                 await cayepSwal.fire({
//                     icon: "success",
//                     title:
//                         "Course Removed",
//                     text:
//                         "The course has been removed from your planned courses.",
//                     timer: 1800,
//                     showConfirmButton:
//                         false,
//                 });
//             } catch (error) {
//                 console.error(
//                     "Remove course error:",
//                     error
//                 );

//                 await Promise.all([
//                     fetchCart(),
//                     fetchCourses(),
//                 ]);

//                 await cayepSwal.fire({
//                     icon: "error",
//                     title:
//                         "Unable to Remove Course",
//                     text: getErrorMessage(
//                         error,
//                         "The planned course could not be removed."
//                     ),
//                 });
//             } finally {
//                 setCartLoading(false);
//             }
//         };

//     const submitPlan =
//         async () => {
//             if (!cart.length) {
//                 await cayepSwal.fire({
//                     icon: "warning",
//                     title:
//                         "No Courses Selected",
//                     text:
//                         "Please add at least one course before submitting.",
//                 });

//                 return;
//             }

//             if (!paymentMethod) {
//                 await cayepSwal.fire({
//                     icon: "warning",
//                     title:
//                         "Payment Method Required",
//                     text:
//                         "Please select a payment method.",
//                 });

//                 return;
//             }

//             if (
//                 paymentMethod ===
//                     "bank_deposit" &&
//                 !bankName
//             ) {
//                 await cayepSwal.fire({
//                     icon: "warning",
//                     title:
//                         "Bank Required",
//                     text:
//                         "Please select the bank used for your payment.",
//                 });

//                 return;
//             }

//             if (
//                 !transactionReference.trim()
//             ) {
//                 await cayepSwal.fire({
//                     icon: "warning",
//                     title:
//                         "Transaction Reference Required",
//                     text:
//                         "Please enter your payment transaction reference.",
//                 });

//                 return;
//             }

//             if (
//                 !paymentAmount ||
//                 Number(paymentAmount) <=
//                     0
//             ) {
//                 await cayepSwal.fire({
//                     icon: "warning",
//                     title:
//                         "Payment Amount Required",
//                     text:
//                         "Please enter a valid payment amount.",
//                 });

//                 return;
//             }

//             const result =
//                 await cayepSwal.fire({
//                     icon: "question",
//                     title:
//                         "Submit Course Plan?",
//                     html: `
//                         <div style="text-align:left;">
//                             <p>
//                                 You are submitting
//                                 <strong>${cart.length}</strong>
//                                 course${
//                                     cart.length === 1
//                                         ? ""
//                                         : "s"
//                                 }.
//                             </p>
//                             <p>
//                                 Total course value:
//                                 <strong>
//                                     LRD ${formatMoney(
//                                         cartTotal
//                                     )}
//                                 </strong>
//                             </p>
//                         </div>
//                     `,
//                     showCancelButton: true,
//                     confirmButtonText:
//                         "Submit Plan",
//                     cancelButtonText:
//                         "Review",
//                 });

//             if (
//                 !result.isConfirmed
//             ) {
//                 return;
//             }

//             try {
//                 setSubmitting(true);

//                 await api.post(
//                     "/api/course-learning/orders/submit/",
//                     {
//                         payment_method:
//                             paymentMethod,

//                         bank_name:
//                             paymentMethod ===
//                             "bank_deposit"
//                                 ? bankName
//                                 : "",

//                         reference_number:
//                             transactionReference.trim(),

//                         notes: "",
//                     }
//                 );

//                 setCart([]);
//                 setTransactionReference(
//                     ""
//                 );
//                 setPaymentAmount("");
//                 setBankName("");

//                 await Promise.all([
//                     fetchCart(),
//                     fetchCourses(),
//                 ]);

//                 await cayepSwal.fire({
//                     icon: "success",
//                     title:
//                         "Course Plan Submitted",
//                     text:
//                         "Your selected courses have been submitted successfully for processing.",
//                     confirmButtonText:
//                         "Continue",
//                 });

//                 setCartOpen(false);
//             } catch (error) {
//                 console.error(
//                     "Submit plan error:",
//                     error
//                 );

//                 const errorCode =
//                     getErrorCode(error);

//                 await Promise.all([
//                     fetchCart(),
//                     fetchCourses(),
//                 ]);

//                 if (
//                     errorCode ===
//                     "ALREADY_ENROLLED"
//                 ) {
//                     await cayepSwal.fire({
//                         icon: "info",
//                         title:
//                             "Course Already Enrolled",
//                         text:
//                             "One or more selected courses are already enrolled for the current training year. Your planned courses have been refreshed.",
//                     });

//                     return;
//                 }

//                 if (
//                     errorCode ===
//                     "COURSE_FULL"
//                 ) {
//                     await cayepSwal.fire({
//                         icon: "warning",
//                         title:
//                             "Course Capacity Reached",
//                         text:
//                             "One of the selected courses has reached its capacity. Your planned courses have been refreshed.",
//                     });

//                     return;
//                 }

//                 await cayepSwal.fire({
//                     icon: "error",
//                     title:
//                         "Submission Failed",
//                     text: getErrorMessage(
//                         error,
//                         "Your course plan could not be submitted."
//                     ),
//                 });
//             } finally {
//                 setSubmitting(false);
//             }
//         };

//     const cartTotal = useMemo(() => {
//         return cart.reduce(
//             (total, item) => {
//                 const course =
//                     getCartCourse(
//                         item
//                     );

//                 const price =
//                     course?.current_price ??
//                     course?.price ??
//                     item?.unit_price ??
//                     0;

//                 return (
//                     total +
//                     Number(
//                         price || 0
//                     )
//                 );
//             },
//             0
//         );
//     }, [
//         cart,
//         getCartCourse,
//     ]);

//     const categories =
//         useMemo(() => {
//             const values =
//                 courses
//                     .map(
//                         (course) =>
//                             course?.category
//                     )
//                     .filter(Boolean);

//             return [
//                 ...new Set(
//                     values
//                 ),
//             ];
//         }, [courses]);

//     const levels =
//         useMemo(() => {
//             const values =
//                 courses
//                     .map(
//                         (course) =>
//                             course?.level
//                     )
//                     .filter(Boolean);

//             return [
//                 ...new Set(
//                     values
//                 ),
//             ];
//         }, [courses]);

//     const handlePreviousPage =
//         () => {
//             setPage(
//                 (current) =>
//                     Math.max(
//                         1,
//                         current - 1
//                     )
//             );
//         };

//     const handleNextPage =
//         () => {
//             setPage(
//                 (current) =>
//                     Math.min(
//                         totalPages,
//                         current + 1
//                     )
//             );
//         };

//     return (
//         <div className="cayep-plan-page">
//             <style>{`
//                 .cayep-plan-page {
//                     min-height: 100vh;
//                     background: #F4F7F9;
//                     color: #142536;
//                     padding: 24px;
//                 }

//                 .cayep-plan-container {
//                     width: 100%;
//                     max-width: 1440px;
//                     margin: 0 auto;
//                 }

//                 .cayep-plan-header {
//                     display: flex;
//                     justify-content: space-between;
//                     align-items: center;
//                     gap: 20px;
//                     margin-bottom: 22px;
//                 }

//                 .cayep-plan-heading {
//                     display: flex;
//                     align-items: center;
//                     gap: 14px;
//                 }

//                 .cayep-plan-heading-icon {
//                     width: 52px;
//                     height: 52px;
//                     border-radius: 14px;
//                     background: #062A4B;
//                     color: #F6C944;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     font-size: 21px;
//                     flex-shrink: 0;
//                 }

//                 .cayep-plan-heading h1 {
//                     margin: 0;
//                     font-size: 25px;
//                     font-weight: 800;
//                     color: #062A4B;
//                 }

//                 .cayep-plan-heading p {
//                     margin: 4px 0 0;
//                     color: #6B7280;
//                     font-size: 14px;
//                 }

//                 .cayep-cart-toggle {
//                     border: 0;
//                     background: #062A4B;
//                     color: #FFFFFF;
//                     border-radius: 11px;
//                     min-height: 46px;
//                     padding: 0 17px;
//                     display: flex;
//                     align-items: center;
//                     gap: 9px;
//                     cursor: pointer;
//                     font-weight: 700;
//                     box-shadow: 0 5px 15px rgba(6, 42, 75, 0.14);
//                 }

//                 .cayep-cart-toggle:hover {
//                     background: #063A6B;
//                 }

//                 .cayep-cart-count {
//                     min-width: 24px;
//                     height: 24px;
//                     padding: 0 6px;
//                     border-radius: 20px;
//                     background: #F6C944;
//                     color: #062A4B;
//                     display: inline-flex;
//                     align-items: center;
//                     justify-content: center;
//                     font-size: 12px;
//                     font-weight: 800;
//                 }

//                 .cayep-eligibility {
//                     border: 1px solid #E0E7EC;
//                     background: #FFFFFF;
//                     border-radius: 14px;
//                     padding: 15px 17px;
//                     margin-bottom: 18px;
//                     display: flex;
//                     align-items: center;
//                     gap: 12px;
//                     box-shadow: 0 3px 12px rgba(20, 37, 54, 0.04);
//                 }

//                 .cayep-eligibility.success {
//                     border-color: rgba(79, 174, 34, 0.35);
//                     background: #F7FCF4;
//                 }

//                 .cayep-eligibility.warning {
//                     border-color: rgba(192, 117, 13, 0.3);
//                     background: #FFF9F0;
//                 }

//                 .cayep-eligibility-icon {
//                     font-size: 19px;
//                     color: #4FAE22;
//                 }

//                 .cayep-eligibility.warning .cayep-eligibility-icon {
//                     color: #C0750D;
//                 }

//                 .cayep-eligibility-text strong {
//                     display: block;
//                     font-size: 14px;
//                     color: #142536;
//                 }

//                 .cayep-eligibility-text span {
//                     display: block;
//                     margin-top: 2px;
//                     color: #6B7280;
//                     font-size: 13px;
//                 }

//                 .cayep-filters {
//                     background: #FFFFFF;
//                     border: 1px solid #E0E7EC;
//                     border-radius: 14px;
//                     padding: 14px;
//                     margin-bottom: 20px;
//                     display: grid;
//                     grid-template-columns: minmax(220px, 1fr) 190px 170px;
//                     gap: 10px;
//                 }

//                 .cayep-filter-input,
//                 .cayep-filter-select {
//                     width: 100%;
//                     height: 44px;
//                     border: 1px solid #D7E0E6;
//                     background: #FFFFFF;
//                     border-radius: 9px;
//                     padding: 0 13px;
//                     color: #142536;
//                     outline: none;
//                     font-size: 14px;
//                     box-sizing: border-box;
//                 }

//                 .cayep-filter-input:focus,
//                 .cayep-filter-select:focus {
//                     border-color: #0F6DAB;
//                     box-shadow: 0 0 0 3px rgba(15, 109, 171, 0.08);
//                 }

//                 .cayep-course-grid {
//                     display: grid;
//                     grid-template-columns: repeat(3, minmax(0, 1fr));
//                     gap: 18px;
//                 }

//                 .cayep-course-card {
//                     background: #FFFFFF;
//                     border: 1px solid #E0E7EC;
//                     border-radius: 15px;
//                     overflow: hidden;
//                     box-shadow: 0 5px 16px rgba(20, 37, 54, 0.045);
//                     display: flex;
//                     flex-direction: column;
//                     min-width: 0;
//                 }

//                 .cayep-course-image {
//                     height: 190px;
//                     background: #E9EEF2;
//                     position: relative;
//                     overflow: hidden;
//                 }

//                 .cayep-course-image img {
//                     width: 100%;
//                     height: 100%;
//                     display: block;
//                     object-fit: cover;
//                 }

//                 .cayep-course-image-placeholder {
//                     width: 100%;
//                     height: 100%;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     color: #062A4B;
//                     font-size: 40px;
//                 }

//                 .cayep-course-category {
//                     position: absolute;
//                     left: 12px;
//                     top: 12px;
//                     background: rgba(6, 42, 75, 0.92);
//                     color: #FFFFFF;
//                     border-radius: 7px;
//                     padding: 6px 9px;
//                     font-size: 11px;
//                     font-weight: 700;
//                     text-transform: uppercase;
//                     letter-spacing: 0.25px;
//                 }

//                 .cayep-course-body {
//                     padding: 16px;
//                     display: flex;
//                     flex-direction: column;
//                     flex: 1;
//                 }

//                 .cayep-course-title {
//                     margin: 0 0 8px;
//                     font-size: 17px;
//                     line-height: 1.35;
//                     font-weight: 800;
//                     color: #062A4B;
//                 }

//                 .cayep-course-description {
//                     margin: 0 0 13px;
//                     color: #6B7280;
//                     font-size: 13px;
//                     line-height: 1.55;
//                     display: -webkit-box;
//                     -webkit-line-clamp: 3;
//                     -webkit-box-orient: vertical;
//                     overflow: hidden;
//                 }

//                 .cayep-course-meta {
//                     display: flex;
//                     flex-direction: column;
//                     gap: 7px;
//                     margin-bottom: 14px;
//                 }

//                 .cayep-course-meta-row {
//                     display: flex;
//                     align-items: center;
//                     gap: 8px;
//                     color: #6B7280;
//                     font-size: 12px;
//                 }

//                 .cayep-course-meta-row svg {
//                     color: #0F6DAB;
//                     width: 13px;
//                     flex-shrink: 0;
//                 }

//                 .cayep-course-slots {
//                     border: 1px solid #E0E7EC;
//                     background: #F8FAFB;
//                     border-radius: 10px;
//                     padding: 10px;
//                     margin-bottom: 14px;
//                     display: flex;
//                     align-items: center;
//                     justify-content: space-between;
//                     gap: 10px;
//                 }

//                 .cayep-course-slot-info {
//                     display: flex;
//                     align-items: center;
//                     gap: 9px;
//                     min-width: 0;
//                 }

//                 .cayep-course-slot-info > svg {
//                     color: #0F6DAB;
//                     flex-shrink: 0;
//                     font-size: 16px;
//                 }

//                 .cayep-course-slot-text {
//                     min-width: 0;
//                 }

//                 .cayep-course-slot-text strong {
//                     display: block;
//                     color: #142536;
//                     font-size: 12px;
//                     font-weight: 800;
//                     line-height: 1.25;
//                 }

//                 .cayep-course-slot-text span {
//                     display: block;
//                     color: #6B7280;
//                     font-size: 11px;
//                     margin-top: 3px;
//                 }

//                 .cayep-slot-badge {
//                     flex-shrink: 0;
//                     border-radius: 20px;
//                     padding: 5px 8px;
//                     background: #EAF6E4;
//                     color: #3C851C;
//                     font-size: 10px;
//                     font-weight: 800;
//                     white-space: nowrap;
//                 }

//                 .cayep-slot-badge.last {
//                     background: #FFF4D6;
//                     color: #A46A00;
//                 }

//                 .cayep-slot-badge.full {
//                     background: #FDECEC;
//                     color: #B42318;
//                 }

//                 .cayep-course-footer {
//                     display: flex;
//                     align-items: center;
//                     justify-content: space-between;
//                     gap: 10px;
//                     margin-top: auto;
//                 }

//                 .cayep-course-price {
//                     color: #062A4B;
//                     font-weight: 900;
//                     font-size: 18px;
//                     white-space: nowrap;
//                 }

//                 .cayep-add-btn {
//                     border: 0;
//                     min-height: 40px;
//                     border-radius: 9px;
//                     padding: 0 13px;
//                     display: inline-flex;
//                     align-items: center;
//                     justify-content: center;
//                     gap: 7px;
//                     background: #062A4B;
//                     color: #FFFFFF;
//                     font-size: 12px;
//                     font-weight: 800;
//                     cursor: pointer;
//                 }

//                 .cayep-add-btn:hover:not(:disabled) {
//                     background: #063A6B;
//                 }

//                 .cayep-add-btn.added {
//                     background: #EAF6E4;
//                     color: #3C851C;
//                     cursor: default;
//                 }

//                 .cayep-add-btn.full {
//                     background: #F1F3F5;
//                     color: #8A929A;
//                     cursor: not-allowed;
//                 }

//                 .cayep-add-btn:disabled {
//                     opacity: 1;
//                 }

//                 .cayep-empty {
//                     background: #FFFFFF;
//                     border: 1px solid #E0E7EC;
//                     border-radius: 15px;
//                     padding: 55px 20px;
//                     text-align: center;
//                     color: #6B7280;
//                     grid-column: 1 / -1;
//                 }

//                 .cayep-empty svg {
//                     color: #C4C7C7;
//                     font-size: 38px;
//                     margin-bottom: 12px;
//                 }

//                 .cayep-empty strong {
//                     display: block;
//                     color: #142536;
//                     font-size: 15px;
//                     margin-bottom: 5px;
//                 }

//                 .cayep-loading {
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     min-height: 250px;
//                     color: #062A4B;
//                     grid-column: 1 / -1;
//                     font-weight: 700;
//                 }

//                 .cayep-pagination {
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     gap: 14px;
//                     margin-top: 22px;
//                 }

//                 .cayep-pagination button {
//                     border: 1px solid #D7E0E6;
//                     background: #FFFFFF;
//                     color: #062A4B;
//                     border-radius: 8px;
//                     min-height: 38px;
//                     padding: 0 13px;
//                     cursor: pointer;
//                     font-weight: 700;
//                 }

//                 .cayep-pagination button:disabled {
//                     color: #A3ABB2;
//                     cursor: not-allowed;
//                     background: #F4F6F7;
//                 }

//                 .cayep-pagination span {
//                     color: #6B7280;
//                     font-size: 13px;
//                     font-weight: 700;
//                 }

//                 .cayep-cart-overlay {
//                     position: fixed;
//                     inset: 0;
//                     background: rgba(6, 15, 22, 0.42);
//                     z-index: 1000;
//                 }

//                 .cayep-cart-panel {
//                     position: fixed;
//                     top: 0;
//                     right: 0;
//                     width: min(440px, 100%);
//                     height: 100vh;
//                     background: #FFFFFF;
//                     z-index: 1001;
//                     box-shadow: -10px 0 35px rgba(6, 42, 75, 0.16);
//                     display: flex;
//                     flex-direction: column;
//                 }

//                 .cayep-cart-header {
//                     padding: 18px;
//                     background: #062A4B;
//                     color: #FFFFFF;
//                     display: flex;
//                     align-items: center;
//                     justify-content: space-between;
//                     gap: 12px;
//                 }

//                 .cayep-cart-header h2 {
//                     margin: 0;
//                     font-size: 18px;
//                 }

//                 .cayep-cart-header p {
//                     margin: 4px 0 0;
//                     color: rgba(255,255,255,0.72);
//                     font-size: 11px;
//                 }

//                 .cayep-cart-header button {
//                     border: 0;
//                     background: transparent;
//                     color: #FFFFFF;
//                     font-size: 23px;
//                     cursor: pointer;
//                     line-height: 1;
//                 }

//                 .cayep-cart-content {
//                     flex: 1;
//                     overflow-y: auto;
//                     padding: 16px;
//                 }

//                 .cayep-cart-item {
//                     border: 1px solid #E0E7EC;
//                     border-radius: 11px;
//                     padding: 10px;
//                     display: flex;
//                     gap: 10px;
//                     margin-bottom: 10px;
//                 }

//                 .cayep-cart-thumb {
//                     width: 62px;
//                     height: 62px;
//                     border-radius: 8px;
//                     background: #EEF2F4;
//                     overflow: hidden;
//                     flex-shrink: 0;
//                 }

//                 .cayep-cart-thumb img {
//                     width: 100%;
//                     height: 100%;
//                     object-fit: cover;
//                 }

//                 .cayep-cart-thumb-placeholder {
//                     width: 100%;
//                     height: 100%;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     color: #062A4B;
//                 }

//                 .cayep-cart-item-info {
//                     min-width: 0;
//                     flex: 1;
//                 }

//                 .cayep-cart-item-title {
//                     color: #062A4B;
//                     font-size: 13px;
//                     font-weight: 800;
//                     margin: 0 0 3px;
//                     line-height: 1.35;
//                 }

//                 .cayep-cart-item-facilitator {
//                     color: #6B7280;
//                     font-size: 11px;
//                     margin-bottom: 6px;
//                 }

//                 .cayep-cart-slot {
//                     display: inline-flex;
//                     align-items: center;
//                     gap: 5px;
//                     color: #3C851C;
//                     background: #F1F9ED;
//                     border-radius: 6px;
//                     padding: 4px 6px;
//                     font-size: 10px;
//                     font-weight: 800;
//                     margin-bottom: 6px;
//                 }

//                 .cayep-cart-slot.last {
//                     color: #A46A00;
//                     background: #FFF7E2;
//                 }

//                 .cayep-cart-slot.full {
//                     color: #B42318;
//                     background: #FDECEC;
//                 }

//                 .cayep-cart-capacity {
//                     color: #6B7280;
//                     font-size: 10px;
//                     margin-left: 4px;
//                 }

//                 .cayep-cart-item-bottom {
//                     display: flex;
//                     align-items: center;
//                     justify-content: space-between;
//                     gap: 8px;
//                 }

//                 .cayep-cart-item-price {
//                     color: #142536;
//                     font-size: 13px;
//                     font-weight: 900;
//                 }

//                 .cayep-cart-remove {
//                     width: 31px;
//                     height: 31px;
//                     border: 0;
//                     border-radius: 7px;
//                     background: #FDECEC;
//                     color: #B42318;
//                     cursor: pointer;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                 }

//                 .cayep-cart-empty {
//                     padding: 45px 15px;
//                     text-align: center;
//                     color: #6B7280;
//                 }

//                 .cayep-cart-empty svg {
//                     font-size: 36px;
//                     color: #C4C7C7;
//                     margin-bottom: 10px;
//                 }

//                 .cayep-payment {
//                     border-top: 1px solid #E0E7EC;
//                     padding: 16px;
//                     background: #FAFBFC;
//                 }

//                 .cayep-payment-title {
//                     margin: 0 0 11px;
//                     color: #062A4B;
//                     font-size: 14px;
//                     font-weight: 900;
//                 }

//                 .cayep-payment-grid {
//                     display: grid;
//                     grid-template-columns: 1fr 1fr;
//                     gap: 9px;
//                 }

//                 .cayep-payment-grid .full {
//                     grid-column: 1 / -1;
//                 }

//                 .cayep-payment label {
//                     display: block;
//                     color: #6B7280;
//                     font-size: 10px;
//                     font-weight: 800;
//                     margin-bottom: 4px;
//                     text-transform: uppercase;
//                 }

//                 .cayep-payment input,
//                 .cayep-payment select {
//                     width: 100%;
//                     height: 39px;
//                     border: 1px solid #D7E0E6;
//                     border-radius: 8px;
//                     padding: 0 9px;
//                     box-sizing: border-box;
//                     background: #FFFFFF;
//                     color: #142536;
//                     outline: none;
//                     font-size: 12px;
//                 }

//                 .cayep-payment input:focus,
//                 .cayep-payment select:focus {
//                     border-color: #0F6DAB;
//                 }

//                 .cayep-cart-total {
//                     margin-top: 13px;
//                     padding-top: 12px;
//                     border-top: 1px solid #E0E7EC;
//                     display: flex;
//                     justify-content: space-between;
//                     align-items: center;
//                 }

//                 .cayep-cart-total span {
//                     color: #6B7280;
//                     font-size: 13px;
//                     font-weight: 700;
//                 }

//                 .cayep-cart-total strong {
//                     color: #062A4B;
//                     font-size: 19px;
//                 }

//                 .cayep-submit-plan {
//                     width: 100%;
//                     min-height: 45px;
//                     margin-top: 12px;
//                     border: 0;
//                     border-radius: 9px;
//                     background: #062A4B;
//                     color: #FFFFFF;
//                     cursor: pointer;
//                     font-size: 13px;
//                     font-weight: 900;
//                 }

//                 .cayep-submit-plan:hover:not(:disabled) {
//                     background: #063A6B;
//                 }

//                 .cayep-submit-plan:disabled {
//                     opacity: 0.55;
//                     cursor: not-allowed;
//                 }

//                 @media (max-width: 1100px) {
//                     .cayep-course-grid {
//                         grid-template-columns: repeat(2, minmax(0, 1fr));
//                     }
//                 }

//                 @media (max-width: 760px) {
//                     .cayep-plan-page {
//                         padding: 15px;
//                     }

//                     .cayep-plan-header {
//                         align-items: flex-start;
//                     }

//                     .cayep-plan-heading h1 {
//                         font-size: 21px;
//                     }

//                     .cayep-filters {
//                         grid-template-columns: 1fr;
//                     }

//                     .cayep-course-grid {
//                         grid-template-columns: 1fr;
//                     }
//                 }

//                 @media (max-width: 520px) {
//                     .cayep-plan-header {
//                         flex-direction: column;
//                     }

//                     .cayep-cart-toggle {
//                         width: 100%;
//                         justify-content: center;
//                     }

//                     .cayep-course-image {
//                         height: 175px;
//                     }

//                     .cayep-course-slots {
//                         align-items: flex-start;
//                     }

//                     .cayep-course-footer {
//                         align-items: flex-end;
//                     }

//                     .cayep-payment-grid {
//                         grid-template-columns: 1fr;
//                     }

//                     .cayep-payment-grid .full {
//                         grid-column: auto;
//                     }
//                 }
//             `}</style>

//             <div className="cayep-plan-container">
//                 <div className="cayep-plan-header">
//                     <div className="cayep-plan-heading">
//                         <div className="cayep-plan-heading-icon">
//                             <FaGraduationCap />
//                         </div>

//                         <div>
//                             <h1>
//                                 Plan Your Courses
//                             </h1>

//                             <p>
//                                 Select one or more courses
//                                 you want to take for your
//                                 current training year.
//                             </p>
//                         </div>
//                     </div>

//                     <button
//                         type="button"
//                         className="cayep-cart-toggle"
//                         onClick={() =>
//                             setCartOpen(true)
//                         }
//                     >
//                         <FaShoppingCart />

//                         Planned Courses

//                         <span className="cayep-cart-count">
//                             {cart.length}
//                         </span>
//                     </button>
//                 </div>

//                 {!eligibilityLoading && (
//                     <div
//                         className={`cayep-eligibility ${
//                             isEligible
//                                 ? "success"
//                                 : "warning"
//                         }`}
//                     >
//                         {isEligible ? (
//                             <FaCheckCircle className="cayep-eligibility-icon" />
//                         ) : (
//                             <FaExclamationTriangle className="cayep-eligibility-icon" />
//                         )}

//                         <div className="cayep-eligibility-text">
//                             <strong>
//                                 {isEligible
//                                     ? "You are eligible to plan courses"
//                                     : "Course planning is currently unavailable"}
//                             </strong>

//                             <span>
//                                 {eligibility?.message ||
//                                     (isEligible
//                                         ? "You can select one or more courses from the catalog below."
//                                         : "Please complete the required eligibility conditions before planning courses.")}
//                             </span>
//                         </div>
//                     </div>
//                 )}

//                 <div className="cayep-filters">
//                     <input
//                         type="search"
//                         className="cayep-filter-input"
//                         placeholder="Search courses..."
//                         value={search}
//                         onChange={(event) =>
//                             setSearch(
//                                 event.target.value
//                             )
//                         }
//                     />

//                     <select
//                         className="cayep-filter-select"
//                         value={category}
//                         onChange={(event) =>
//                             setCategory(
//                                 event.target.value
//                             )
//                         }
//                     >
//                         <option value="">
//                             All Categories
//                         </option>

//                         {categories.map(
//                             (item) => (
//                                 <option
//                                     key={item}
//                                     value={item}
//                                 >
//                                     {item}
//                                 </option>
//                             )
//                         )}
//                     </select>

//                     <select
//                         className="cayep-filter-select"
//                         value={level}
//                         onChange={(event) =>
//                             setLevel(
//                                 event.target.value
//                             )
//                         }
//                     >
//                         <option value="">
//                             All Levels
//                         </option>

//                         {levels.map(
//                             (item) => (
//                                 <option
//                                     key={item}
//                                     value={item}
//                                 >
//                                     {item}
//                                 </option>
//                             )
//                         )}
//                     </select>
//                 </div>

//                 <div className="cayep-course-grid">
//                     {loading ? (
//                         <div className="cayep-loading">
//                             Loading courses...
//                         </div>
//                     ) : courses.length === 0 ? (
//                         <div className="cayep-empty">
//                             <FaBookOpen />

//                             <strong>
//                                 No courses found
//                             </strong>

//                             <span>
//                                 Try changing your
//                                 search or filter
//                                 options.
//                             </span>
//                         </div>
//                     ) : (
//                         courses.map(
//                             (course) => {
//                                 const slotState =
//                                     getSlotState(
//                                         course
//                                     );

//                                 const inCart =
//                                     isInCart(
//                                         course.id
//                                     );

//                                 return (
//                                     <div
//                                         className="cayep-course-card"
//                                         key={
//                                             course.id
//                                         }
//                                     >
//                                         <div className="cayep-course-image">
//                                             {course.thumbnail ||
//                                             course.image ? (
//                                                 <img
//                                                     src={
//                                                         course.thumbnail ||
//                                                         course.image
//                                                     }
//                                                     alt={
//                                                         course.course_title ||
//                                                         course.name
//                                                     }
//                                                 />
//                                             ) : (
//                                                 <div className="cayep-course-image-placeholder">
//                                                     <FaBookOpen />
//                                                 </div>
//                                             )}

//                                             {course.category && (
//                                                 <span className="cayep-course-category">
//                                                     {
//                                                         course.category
//                                                     }
//                                                 </span>
//                                             )}
//                                         </div>

//                                         <div className="cayep-course-body">
//                                             <h2 className="cayep-course-title">
//                                                 {course.course_title ||
//                                                     course.name}
//                                             </h2>

//                                             <p className="cayep-course-description">
//                                                 {course.description ||
//                                                     "Professional training course designed to develop practical knowledge and leadership skills."}
//                                             </p>

//                                             <div className="cayep-course-meta">
//                                                 {course.facilitator && (
//                                                     <div className="cayep-course-meta-row">
//                                                         <FaUserTie />

//                                                         <span>
//                                                             {
//                                                                 course.facilitator
//                                                             }
//                                                         </span>
//                                                     </div>
//                                                 )}

//                                                 {course.level && (
//                                                     <div className="cayep-course-meta-row">
//                                                         <FaGraduationCap />

//                                                         <span>
//                                                             {
//                                                                 course.level
//                                                             }
//                                                         </span>
//                                                     </div>
//                                                 )}

//                                                 {course.duration && (
//                                                     <div className="cayep-course-meta-row">
//                                                         <FaClock />

//                                                         <span>
//                                                             {
//                                                                 course.duration
//                                                             }
//                                                         </span>
//                                                     </div>
//                                                 )}

//                                                 {course.video_url && (
//                                                     <div className="cayep-course-meta-row">
//                                                         <FaVideo />

//                                                         <span>
//                                                             Video learning available
//                                                         </span>
//                                                     </div>
//                                                 )}
//                                             </div>

//                                             <div className="cayep-course-slots">
//                                                 <div className="cayep-course-slot-info">
//                                                     {slotState.full ? (
//                                                         <FaExclamationTriangle />
//                                                     ) : (
//                                                         <FaUsers />
//                                                     )}

//                                                     <div className="cayep-course-slot-text">
//                                                         <strong>
//                                                             {slotState.full
//                                                                 ? "Course Capacity"
//                                                                 : slotState.unlimited
//                                                                 ? "Course Capacity"
//                                                                 : `${slotState.remaining} ${
//                                                                       slotState.remaining ===
//                                                                       1
//                                                                           ? "slot"
//                                                                           : "slots"
//                                                                   } available`}
//                                                         </strong>

//                                                         <span>
//                                                             {slotState.unlimited
//                                                                 ? "Open enrollment"
//                                                                 : `${slotState.taken} of ${slotState.total} ${
//                                                                       slotState.total ===
//                                                                       1
//                                                                           ? "seat"
//                                                                           : "seats"
//                                                                   } occupied`}
//                                                         </span>
//                                                     </div>
//                                                 </div>

//                                                 <span
//                                                     className={`cayep-slot-badge ${
//                                                         slotState.full
//                                                             ? "full"
//                                                             : slotState.lastSlot
//                                                             ? "last"
//                                                             : ""
//                                                     }`}
//                                                 >
//                                                     {getSlotLabel(
//                                                         course
//                                                     )}
//                                                 </span>
//                                             </div>

//                                             <div className="cayep-course-footer">
//                                                 <div className="cayep-course-price">
//                                                     {course.current_price ||
//                                                     course.price
//                                                         ? `LRD ${formatMoney(
//                                                               course.current_price ??
//                                                                   course.price
//                                                           )}`
//                                                         : "Free"}
//                                                 </div>

//                                                 <button
//                                                     type="button"
//                                                     className={`cayep-add-btn ${
//                                                         inCart
//                                                             ? "added"
//                                                             : slotState.full
//                                                             ? "full"
//                                                             : ""
//                                                     }`}
//                                                     disabled={
//                                                         inCart ||
//                                                         slotState.full ||
//                                                         !isEligible ||
//                                                         cartLoading ||
//                                                         refreshingSlots
//                                                     }
//                                                     onClick={() =>
//                                                         addToCart(
//                                                             course
//                                                         )
//                                                     }
//                                                 >
//                                                     {inCart ? (
//                                                         <>
//                                                             <FaCheckCircle />
//                                                             Planned
//                                                         </>
//                                                     ) : slotState.full ? (
//                                                         <>
//                                                             <FaExclamationTriangle />
//                                                             Class Full
//                                                         </>
//                                                     ) : (
//                                                         <>
//                                                             <FaPlus />
//                                                             Add Course
//                                                         </>
//                                                     )}
//                                                 </button>
//                                             </div>
//                                         </div>
//                                     </div>
//                                 );
//                             }
//                         )
//                     )}
//                 </div>

//                 {!loading &&
//                     courses.length > 0 && (
//                         <div className="cayep-pagination">
//                             <button
//                                 type="button"
//                                 disabled={
//                                     page <= 1
//                                 }
//                                 onClick={
//                                     handlePreviousPage
//                                 }
//                             >
//                                 Previous
//                             </button>

//                             <span>
//                                 Page {page} of{" "}
//                                 {totalPages}
//                             </span>

//                             <button
//                                 type="button"
//                                 disabled={
//                                     page >=
//                                     totalPages
//                                 }
//                                 onClick={
//                                     handleNextPage
//                                 }
//                             >
//                                 Next
//                             </button>
//                         </div>
//                     )}
//             </div>

//             {cartOpen && (
//                 <>
//                     <div
//                         className="cayep-cart-overlay"
//                         onClick={() =>
//                             setCartOpen(false)
//                         }
//                     />

//                     <aside className="cayep-cart-panel">
//                         <div className="cayep-cart-header">
//                             <div>
//                                 <h2>
//                                     Planned Courses
//                                 </h2>

//                                 <p>
//                                     {cart.length} course
//                                     {cart.length === 1
//                                         ? ""
//                                         : "s"} selected
//                                 </p>
//                             </div>

//                             <button
//                                 type="button"
//                                 onClick={() =>
//                                     setCartOpen(
//                                         false
//                                     )
//                                 }
//                                 aria-label="Close"
//                             >
//                                 ×
//                             </button>
//                         </div>

//                         <div className="cayep-cart-content">
//                             {cartLoading ? (
//                                 <div className="cayep-cart-empty">
//                                     Loading planned
//                                     courses...
//                                 </div>
//                             ) : cart.length === 0 ? (
//                                 <div className="cayep-cart-empty">
//                                     <FaShoppingCart />

//                                     <div>
//                                         You have not
//                                         planned any
//                                         courses yet.
//                                     </div>
//                                 </div>
//                             ) : (
//                                 cart.map(
//                                     (item) => {
//                                         const course =
//                                             getCartCourse(
//                                                 item
//                                             );

//                                         const planId =
//                                             getPlanId(
//                                                 item
//                                             );

//                                         const slotState =
//                                             getSlotState(
//                                                 course
//                                             );

//                                         return (
//                                             <div
//                                                 className="cayep-cart-item"
//                                                 key={
//                                                     planId ||
//                                                     course?.id
//                                                 }
//                                             >
//                                                 <div className="cayep-cart-thumb">
//                                                     {course?.thumbnail ||
//                                                     course?.image ? (
//                                                         <img
//                                                             src={
//                                                                 course.thumbnail ||
//                                                                 course.image
//                                                             }
//                                                             alt={
//                                                                 course.course_title ||
//                                                                 course.name
//                                                             }
//                                                         />
//                                                     ) : (
//                                                         <div className="cayep-cart-thumb-placeholder">
//                                                             <FaBookOpen />
//                                                         </div>
//                                                     )}
//                                                 </div>

//                                                 <div className="cayep-cart-item-info">
//                                                     <h3 className="cayep-cart-item-title">
//                                                         {course?.course_title ||
//                                                             course?.name}
//                                                     </h3>

//                                                     {course?.facilitator && (
//                                                         <div className="cayep-cart-item-facilitator">
//                                                             {
//                                                                 course.facilitator
//                                                             }
//                                                         </div>
//                                                     )}

//                                                     <div
//                                                         className={`cayep-cart-slot ${
//                                                             slotState.full
//                                                                 ? "full"
//                                                                 : slotState.lastSlot
//                                                                 ? "last"
//                                                                 : ""
//                                                         }`}
//                                                     >
//                                                         {slotState.full ? (
//                                                             <FaExclamationTriangle />
//                                                         ) : (
//                                                             <FaUsers />
//                                                         )}

//                                                         <span>
//                                                             {slotState.full
//                                                                 ? "Class is currently full"
//                                                                 : slotState.unlimited
//                                                                 ? "Open enrollment"
//                                                                 : `${slotState.remaining} ${
//                                                                       slotState.remaining ===
//                                                                       1
//                                                                           ? "slot"
//                                                                           : "slots"
//                                                                   } available`}
//                                                         </span>

//                                                         {!slotState.unlimited && (
//                                                             <span className="cayep-cart-capacity">
//                                                                 {
//                                                                     slotState.taken
//                                                                 }
//                                                                 /
//                                                                 {
//                                                                     slotState.total
//                                                                 }
//                                                             </span>
//                                                         )}
//                                                     </div>

//                                                     <div className="cayep-cart-item-bottom">
//                                                         <span className="cayep-cart-item-price">
//                                                             {course?.current_price ||
//                                                             course?.price
//                                                                 ? `LRD ${formatMoney(
//                                                                       course.current_price ??
//                                                                           course.price
//                                                                   )}`
//                                                                 : "Free"}
//                                                         </span>

//                                                         <button
//                                                             type="button"
//                                                             className="cayep-cart-remove"
//                                                             disabled={
//                                                                 cartLoading ||
//                                                                 !planId
//                                                             }
//                                                             onClick={() =>
//                                                                 removeFromCart(
//                                                                     planId
//                                                                 )
//                                                             }
//                                                             title="Remove course"
//                                                         >
//                                                             <FaTrash />
//                                                         </button>
//                                                     </div>
//                                                 </div>
//                                             </div>
//                                         );
//                                     }
//                                 )
//                             )}
//                         </div>

//                         {cart.length > 0 && (
//                             <div className="cayep-payment">
//                                 <h3 className="cayep-payment-title">
//                                     Payment Information
//                                 </h3>

//                                 <div className="cayep-payment-grid">
//                                     <div>
//                                         <label>
//                                             Payment
//                                             Method
//                                         </label>

//                                         <select
//                                             value={
//                                                 paymentMethod
//                                             }
//                                             onChange={(
//                                                 event
//                                             ) =>
//                                                 setPaymentMethod(
//                                                     event
//                                                         .target
//                                                         .value
//                                                 )
//                                             }
//                                         >
//                                             <option value="bank_deposit">
//                                                 Bank
//                                                 Deposit
//                                             </option>

//                                             <option value="cash">
//                                                 Cash
//                                             </option>

//                                             <option value="mobile_money">
//                                                 Mobile
//                                                 Money
//                                             </option>
//                                         </select>
//                                     </div>

//                                     {paymentMethod ===
//                                         "bank_deposit" && (
//                                         <div>
//                                             <label>
//                                                 Bank
//                                             </label>

//                                             <select
//                                                 value={
//                                                     bankName
//                                                 }
//                                                 onChange={(
//                                                     event
//                                                 ) =>
//                                                     setBankName(
//                                                         event
//                                                             .target
//                                                             .value
//                                                     )
//                                                 }
//                                             >
//                                                 <option value="">
//                                                     Select
//                                                     Bank
//                                                 </option>

//                                                 {BANKS.map(
//                                                     (
//                                                         bank
//                                                     ) => (
//                                                         <option
//                                                             key={
//                                                                 bank
//                                                             }
//                                                             value={
//                                                                 bank
//                                                             }
//                                                         >
//                                                             {
//                                                                 bank
//                                                             }
//                                                         </option>
//                                                     )
//                                                 )}
//                                             </select>
//                                         </div>
//                                     )}

//                                     <div>
//                                         <label>
//                                             Payment
//                                             Amount
//                                         </label>

//                                         <input
//                                             type="number"
//                                             min="0"
//                                             step="0.01"
//                                             value={
//                                                 paymentAmount
//                                             }
//                                             onChange={(
//                                                 event
//                                             ) =>
//                                                 setPaymentAmount(
//                                                     event
//                                                         .target
//                                                         .value
//                                                 )
//                                             }
//                                             placeholder="0.00"
//                                         />
//                                     </div>

//                                     <div>
//                                         <label>
//                                             Transaction
//                                             Reference
//                                         </label>

//                                         <input
//                                             type="text"
//                                             value={
//                                                 transactionReference
//                                             }
//                                             onChange={(
//                                                 event
//                                             ) =>
//                                                 setTransactionReference(
//                                                     event
//                                                         .target
//                                                         .value
//                                                 )
//                                             }
//                                             placeholder="Transaction reference"
//                                         />
//                                     </div>
//                                 </div>

//                                 <div className="cayep-cart-total">
//                                     <span>
//                                         {cart.length} Course
//                                         {cart.length === 1
//                                             ? ""
//                                             : "s"} Total
//                                     </span>

//                                     <strong>
//                                         LRD{" "}
//                                         {formatMoney(
//                                             cartTotal
//                                         )}
//                                     </strong>
//                                 </div>

//                                 <button
//                                     type="button"
//                                     className="cayep-submit-plan"
//                                     disabled={
//                                         submitting ||
//                                         cartLoading ||
//                                         refreshingSlots
//                                     }
//                                     onClick={
//                                         submitPlan
//                                     }
//                                 >
//                                     {submitting
//                                         ? "Submitting..."
//                                         : `Submit ${cart.length} Course${
//                                               cart.length ===
//                                               1
//                                                   ? ""
//                                                   : "s"
//                                           }`}
//                                 </button>
//                             </div>
//                         )}
//                     </aside>
//                 </>
//             )}
//         </div>
//     );
// };

// export default PlanCoursesPage;

